import { Epic, ofType } from 'redux-observable';
import { Action } from 'redux';
import {
  arrayOfObjectsToString,
  concatStartAndEndDate,
  concatStringWithSemiColon,
  getEmailByJwtAuthTokenCookie,
  getMatchingPriceType,
  getQueryParam,
  isBooking,
  retrieveExperience,
} from '../../../genericUtils/analyticsUtils';
import { trackEvent } from '../../../analytics/aemAnalytics';
import { mscGet, mscSet } from '../../../utilFuncs/mscStorageClient';
import {
  trackAddToCart,
  trackAddToWishlist,
  trackBeginCheckout,
  trackCheckoutCabinLocation,
  trackCheckoutCabinPreferences,
  trackCheckoutCabinTypeDetails,
  trackCheckoutCabinTypeSelection,
  trackCheckoutProgressGuestAndCabin,
  trackCruiseShare,
  trackExtraServices,
  trackExtraServicesList,
  trackHoldOptionEvent,
  trackInsuranceInfo,
  trackPageView,
  trackPassengersDetails,
  trackPaymentFailed,
  trackPaymentInfo,
  trackPurchase,
  trackPurchaseFailed,
  trackSearchCruise,
  trackSearchExcursionEvent,
  trackSelectItem,
  trackUpgradeOffer,
  trackViewCart,
  trackViewItem,
  trackViewItemList,
} from '../../slices/analyticsSlice';
import { RootState, store } from '../../store/index';
import { catchError, ignoreElements, map } from 'rxjs/operators';
import { of } from 'rxjs';
import { saltedHash } from '../../../genericUtils/encodeDecodeUtils';
import { CabinTypeEnum, Hit } from '../../../../../../interfaces/src/lib/types/index';
import {
  getAllowedCardNetworks,
  getAllowedPaymentButtons,
  getPaymentMethods,
  getRibbonsConfig,
} from '../../../utilFuncs/contextAwareUtils';
import { isDealsPage } from '../../../utilFuncs/dealsManagement';
import { getCountryCodeFromMetaTag } from '../../../../index';
import { CruiseCardRibbon } from '@msc-b2c/interfaces/interfaces';

declare global {
  interface Window {
    didomiOnReady?: any[];
    didomiState?: {
      didomiVendorsEnabled?: any;
    };
  }
}

// Utility function to create an epic for tracking events
// This function generates an epic that listens for a specific action type,
// extracts data using the provided getPayload function, and tracks the event.
const createAnalyticsEpic =
  (
    actionType: string,
    getPayload: (payload: unknown, state: RootState) => object,
    eventName: string
  ): Epic<Action, Action, RootState> =>
  (action$, state$) =>
    action$.pipe(
      ofType(actionType),
      map((action) => {
        const payload = getPayload((action as any).payload || state$.value, state$.value);
        trackEvent(eventName, payload); // Call to the tracking function with event name and payload
      }),
      catchError((error) => {
        console.error(`Error in ${eventName} epic:`, error);
        return of(); // Return empty observable on error
      }),
      ignoreElements() // Prevents the observable from emitting further items
    );

const getAgeGroup = (birthDateString?: string): string => {
  if (!birthDateString) return '';
  const birthDate = new Date(birthDateString);
  const today = new Date();

  let age = today.getFullYear() - birthDate.getFullYear();

  const isBirthdayPassed =
    today.getMonth() > birthDate.getMonth() ||
    (today.getMonth() === birthDate.getMonth() && today.getDate() >= birthDate.getDate());
  if (!isBirthdayPassed) {
    age--;
  }

  if (age < 18) return '18-';
  if (age >= 18 && age <= 24) return '18 to 24';
  if (age >= 25 && age <= 34) return '25 to 34';
  if (age >= 35 && age <= 44) return '35 to 44';
  if (age >= 45 && age <= 54) return '45 to 54';
  if (age >= 55 && age <= 64) return '55 to 64';
  if (age >= 65) return '65+';

  return '';
};

// Helper function to get user information
// This extracts user data that is common across various analytics events
const getUserInfo = (state: RootState) => ({
  user_id:
    getEmailByJwtAuthTokenCookie() != ''
      ? saltedHash(getEmailByJwtAuthTokenCookie())
      : 'not available',
  user_status: 'not available', // TODO: Update when private area is integrated
  user_typology: saltedHash(mscGet<string>('session', 'tsk') || '') || 'not available',
  visitor_is_customer:
    mscGet<string>('session', 'vic') === 'true'
      ? 'yes'
      : mscGet<string>('session', 'vic') === null
      ? 'not available'
      : 'no',
  membership_status:
    getEmailByJwtAuthTokenCookie() != ''
      ? (state?.bookingStepsStore?.firstCabinSelections?.voyagersMembersInfo &&
          state?.bookingStepsStore?.firstCabinSelections?.voyagersMembersInfo?.length > 0) ||
        (state?.bookingStepsStore?.secondCabinSelections?.voyagersMembersInfo &&
          state?.bookingStepsStore?.secondCabinSelections?.voyagersMembersInfo?.length > 0)
        ? state?.bookingStepsStore?.firstCabinSelections?.voyagersMembersInfo[0]?.memberInfo
            ?.membership?.cardType
        : ''
      : 'not available',
});

const constructItemCategory2 = (data: { hasAir: boolean }) => {
  let itemCategory2 = 'yes';

  if (
    data?.hasAir ||
    getQueryParam('isFlightIncluded') === 'true' ||
    getQueryParam('flyAndCruise') === 'hasAir'
  ) {
    itemCategory2 += ':flight';
  }

  if (store?.getState().transferData?.hasTrains) {
    itemCategory2 += ':train';
  }

  const tags = store?.getState()?.genericFilter?.tag || [];

  const hasDrinksIncluded = tags?.some(
    (t: { value: string; isFromURL?: boolean; useInSearch?: boolean }) =>
      t?.value === 'DRINKS_INCLUDED' && (t?.isFromURL || t?.useInSearch)
  );

  if (
    hasDrinksIncluded ||
    window.location?.href?.includes('DRINKS_INCLUDED') ||
    window.location?.href?.includes('PRDRINK')
  ) {
    itemCategory2 += ':drinks';
  }

  //Code to be inserted when info about hotel and transfer is available
  //..........
  // if(store?.getState().transferData?.hasBus){
  //   itemCategory2 += ':bus';
  // }

  if (itemCategory2 == 'yes') itemCategory2 = 'no:no bundle';

  return itemCategory2;
};

const calculateItemName = (data: { commArea: { key: string }[]; itineraryName: string }) => {
  const itemName =
    data?.commArea && data?.commArea[0]?.key?.toLowerCase()
      ? data?.commArea && data?.commArea[0]?.key?.toLowerCase()
      : '';

  mscSet('session', 'itemName', itemName);

  return itemName;
};

// Helper function to map item data
// This function standardizes the structure of item data used in different events (in the search flow)
let mapItemCounter = 0;
let itemListNameHelper = '';
const listOrder: { [key: string]: string | number } = {};
let userState: RootState;
let finalIndex: number;
const mapItemData = (data: any, state: RootState) => {
  // Increment the counter each time mapItemData is called
  userState = state;

  mscSet('session', 'promId', getPromotionId(data, state));
  mapItemCounter++;
  if (itemListNameHelper === '') {
    itemListNameHelper = window?.location?.pathname?.replace('.html', '');
    mscSet('session', 'item_list_name', window?.location?.pathname?.replace('.html', ''));
  }

  if (mapItemCounter < 1000) {
    listOrder[data?.cruiseID] =
      mapItemCounter +
      state?.search?.itineraryData?.hitsPerPage * state?.pagination?.activePageIndex;
    mscSet('session', 'item_order', listOrder);
  }

  if (mapItemCounter >= 1000) {
    const itemOrders = (mscGet<Record<string, number>>('session', 'item_order') ?? {}) as Record<
      string,
      number
    >;
    const itemId = data?.cruiseID ? data?.cruiseID : '';
    if (itemId) {
      finalIndex = itemOrders[itemId];
    }
  } else {
    finalIndex = mapItemCounter;
  }

  mscSet('session', 'finalIndex', finalIndex);
  const it3 = data?.category?.value?.toLowerCase() ? data?.category?.value?.toLowerCase() : '';
  mscSet('session', 'it3', it3);

  let couponVal = '';
  if (calculateDiscount(data, state) > 0) {
    if (!isDealsPage()) {
      if (data?.pricesAfterDiscount?.strikethroughPrice?.cabinPrice > 0) {
        couponVal =
          data?.prices?.strikethroughPrice?.adultPrice > 0
            ? 'strk-' + (data?.priceType?.toLowerCase() ? data?.priceType?.toLowerCase() : '')
            : data?.priceType != ''
            ? 'strk-' + data?.priceType?.toLowerCase()
            : '';
      } else couponVal = '';
    } else {
      couponVal =
        data?.prices?.strikethroughPrice?.adultPrice > 0
          ? 'strk-' + (data?.priceType?.toLowerCase() ? data?.priceType?.toLowerCase() : '')
          : data?.priceType != ''
          ? 'strk-' + data?.priceType?.toLowerCase()
          : '';
    }
  }

  mscSet('session', 'itemLevelCoupon', couponVal);
  const cruise_canvas =
    'season' in (state?.cruiseCard?.selectedCruiseCard || {})
      ? state.cruiseCard.selectedCruiseCard?.season?.toLowerCase()
      : data?.season
      ? data?.season?.toLowerCase()
      : '';
  mscSet('session', 'cruise_canvas', cruise_canvas);

  const strk = !isDealsPage()
    ? data?.pricesAfterDiscount?.strikethroughPrice?.cabinPrice > 0
      ? 'yes'
      : 'no'
    : calculateDiscount(data, state) > 0
    ? 'yes'
    : 'no';
  mscSet('session', 'hasStrikethrough', strk);

  const virtualPageName = mscGet<string>('session', 'virtualPageName') || '';
  const itemListName = mscGet<string>('session', 'itemListName'); //'';

  //SET RIBBONS
  let cruise_promo_badge = '';

  //Default ribbon is retrieved from the itinerary data of the first API called from itinerary index when cards are loaded
  cruise_promo_badge =
    data?.ribbonConfiguration
      ?.map((ribbon: { ribbonText: string }) => ribbon?.ribbonText)
      .join(', ')
      .toLowerCase() || '';

  const results = (userState?.search?.cruiseData?.hits as any)?.results;

  // // case - opening dateslayer with multiple results
  const resultsWithFlights = results?.[1]?.hits ?? [];
  const resultsWithoutFlights = results?.[2]?.hits ?? [];
  const mergedResultsWithFlights = [...resultsWithFlights, ...resultsWithoutFlights];

  //FLIGHT LOGIC FOR RIBBONS
  const flightAvailabilityFromSession = mscGet<Record<string, any>>(
    'session',
    'flightAvailability'
  );
  let showFlightRibbons = false;
  let showIncludedFlights = false;
  if (flightAvailabilityFromSession) {
    //TO DO : MOVE IT TO STATE
    const flightAvailability = flightAvailabilityFromSession ?? {};

    if (flightAvailability && data?.cruiseID in flightAvailability) {
      showFlightRibbons = flightAvailability[data.cruiseID].flightAvailability;
      showIncludedFlights = data.hasAir ? flightAvailability[data.cruiseID].flightIncluded : false;
    }
    if (data.hasAir) {
      showFlightRibbons = true;
      showIncludedFlights = true;
    }
  }

  const flightsRibbons = getRibbonsConfig().filter(
    (ribbonConfig) => ribbonConfig.flightsAvailable || ribbonConfig.flightsIncluded
  );

  let flightsRibbonsWithFlights: CruiseCardRibbon[] = [];

  if (showIncludedFlights) {
    flightsRibbonsWithFlights = flightsRibbons.filter((ribbon) => ribbon.flightsIncluded);
  } else {
    flightsRibbonsWithFlights = flightsRibbons.filter((ribbon) => ribbon.flightsAvailable);
  }

  //matchingHit is found when i click a cruise from dateslayer to go to BF
  const matchingHit = mergedResultsWithFlights.find(
    (hit: {
      id: { key: string };
      shipCd: { key: string };
      disembkPort: { key: string };
      embkPort: { key: string };
    }) => hit?.id === data?.id
  );

  if (matchingHit) {
    cruise_promo_badge = matchingHit.ribbonConfiguration
      .map((item: any) => item.ribbonText)
      .join(',');

    if (flightAvailabilityFromSession) {
      const flightAvailability = flightAvailabilityFromSession ?? {};
      if (flightAvailability && matchingHit?.cruiseID in flightAvailability) {
        showFlightRibbons = flightAvailability[matchingHit.cruiseID].flightAvailability;
        showIncludedFlights = matchingHit.hasAir
          ? flightAvailability[matchingHit.cruiseID].flightIncluded
          : false;
      }
      if (matchingHit.hasAir) {
        showFlightRibbons = true;
        showIncludedFlights = true;
      }
    }
  }

  const paxType = getPaxType(state?.search?.params?.filters, data);
  const ribbonWithoutFlights = cruise_promo_badge;
  const ribbonsWithFlights = ribbonWithoutFlights
    ? ribbonWithoutFlights
        .split(',')
        .concat(flightsRibbonsWithFlights.map((item: any) => item.ribbonLabel))
    : flightsRibbonsWithFlights.map((item: any) => item.ribbonLabel);
  cruise_promo_badge =
    showFlightRibbons || (matchingHit && matchingHit.hasAir)
      ? ribbonsWithFlights.join(':').toLowerCase()
      : ribbonWithoutFlights.toLowerCase();

  mscSet('session', 'cruise_promo_badge', cruise_promo_badge);

  /// END OF RIBBONS LOGIC ////

  return {
    item_id: data?.cruiseID ? data?.cruiseID : '',
    item_name: calculateItemName(data),
    coupon: couponVal,
    discount: !isDealsPage()
      ? data?.pricesAfterDiscount?.strikethroughPrice?.cabinPrice > 0
        ? calculateDiscount(data, state)
        : 0
      : calculateDiscount(data, state),
    index: finalIndex || '',
    item_brand: data?.shipCd?.key?.toLowerCase() ? data?.shipCd?.key?.toLowerCase() : '',
    item_category:
      getQueryParam('flyAndCruise') === 'hasAir' ? 'cruiseonly:cruiseflight' : 'cruiseonly',
    item_category2: constructItemCategory2(data),
    item_category3: data?.category?.value?.toLowerCase()
      ? data?.category?.value?.toLowerCase()
      : '',
    item_list_name: itemListName,
    item_variant: 'booking',
    price: !isDealsPage()
      ? data?.pricesAfterDiscount?.cabinPrice
        ? parseFloat(data?.pricesAfterDiscount?.cabinPrice)
        : ''
      : calculateDiscount(data, state) > 0 && paxType
      ? calculatePriceDeals(data, state)
      : data?.prices?.cabinPrice
      ? data?.prices?.cabinPrice
      : '',
    quantity: 1,
    currency: state?.genericConfigs?.configs?.b2cCommerceConfig?.currencyCode?.toLowerCase()
      ? state?.genericConfigs?.configs?.b2cCommerceConfig?.currencyCode?.toLowerCase()
      : '',
    adults_number: (
      state?.guestFilter?.firstCabin?.adults +
      (state?.guestFilter?.secondCabin?.isActive ? state?.guestFilter?.secondCabin?.adults : 0)
    ).toString(),
    cabin_num: state?.guestFilter.secondCabin.isActive ? '2' : '1',
    cruise_length: data?.numberOfNights?.toString() ? data?.numberOfNights?.toString() : '',
    cruise_title: data?.itineraryName?.toLowerCase() ? data?.itineraryName?.toLowerCase() : '',
    cruise_type: data?.embkPort?.key === data?.disembkPort?.key ? 'round trip' : 'oneway',
    cruise_canvas: sessionStorage?.getItem('cruise_canvas')
      ? sessionStorage?.getItem('cruise_canvas')
      : store.getState().cruiseData?.season?.toLowerCase() ?? '',
    cruise_promo_badge: cruise_promo_badge,
    departing_port: data?.embkPort?.key?.toLowerCase() ? data?.embkPort?.key?.toLowerCase() : '',
    arrival_port: data?.disembkPort?.key?.toLowerCase()
      ? data?.disembkPort?.key?.toLowerCase()
      : '',
    number_passengers: state?.guestFilter?.firstCabin?.adults
      ? (
          state?.guestFilter?.firstCabin?.adults +
          state?.guestFilter?.firstCabin?.minors +
          state?.guestFilter?.firstCabin?.juniors +
          (state.guestFilter?.secondCabin?.isActive
            ? state?.guestFilter?.secondCabin?.adults +
              state?.guestFilter?.secondCabin?.minors +
              state?.guestFilter?.secondCabin?.juniors
            : 0)
        ).toString()
      : '0',
    cabin_connected: state?.guestFilter?.secondCabin?.isConnectedCabin ? 'yes' : 'no',
    cabin_accessible:
      state?.guestFilter?.firstCabin?.isAccessible || state?.guestFilter?.secondCabin?.isAccessible
        ? 'yes'
        : 'no',
    price_name: data?.priceDesc?.toLowerCase() ? data?.priceDesc?.toLowerCase() : '',
    price_type: data?.priceType?.toLowerCase() ? data?.priceType?.toLowerCase() : '',
    price_code: data?.priceCode?.toLowerCase() ? data?.priceCode?.toLowerCase() : '',
    promotion_name: getPromotionName(data, state),
    promotion_id: getPromotionId(data, state),
    creative_name: getPromotionName(data, state),
    creative_slot: getPromotionName(data, state) ? 'itinerary card' : '',
    pax_type: getPaxType(state?.search?.params?.filters, data),
    ...(!virtualPageName?.includes('excursion') ? { strikethrough: strk } : {}),
  };
};

const getItemCategory3 = (state: RootState) => {
  //ocean view = out

  const ic1 = (
    state?.bookingStepsStore?.firstCabinSelections?.selectedCabinType ||
    mscGet<string>('session', 'it3')
  )?.toLowerCase();

  const ic2 = (
    state?.bookingStepsStore?.secondCabinSelections?.selectedCabinType ||
    mscGet<string>('session', 'it3')
  )?.toLowerCase();

  let ic3;
  if (ic2 && ic2 != '') ic3 = ic1 + ' : ' + ic2;
  else ic3 = ic1;

  return ic1;
};

const getPromotionId = (data: any, state: RootState) => {
  const paxType = getPaxType(state?.search?.params?.filters, data);
  let appliedPromo = '';
  let tempPromoPrice = 0;
  let promoPrice = 0;
  let prevPromoPrice = 0;
  let specificPromo = '';
  let genDiscountPrice = 0;
  let prevgenDiscountPrice = 0;
  let tempgenDiscountPrice = 0;
  let genPromo = '';
  if (!isBooking()) {
    if (paxType) {
      const pt = getQueryParam('paxType')?.replace('%2C', ',');
      if (pt) {
        const promotions = pt.split(',');
        for (const promo of promotions) {
          for (let i = 0; i < data?.discountCodes.length; i++) {
            if (data?.discountCodes[i]?.paxType == promo) {
              tempPromoPrice = data?.discountCodes[i]?.cabinPrice;
              if (promoPrice == 0) {
                promoPrice = tempPromoPrice;
                prevPromoPrice = promoPrice;
                specificPromo = data?.discountCodes[i]?.codeOfDiscount;
              } else if (prevPromoPrice > tempPromoPrice) {
                promoPrice = tempPromoPrice;
                prevPromoPrice = promoPrice;
                specificPromo = data?.discountCodes[i]?.codeOfDiscount;
              }
            }
          }
        }
      } else {
        for (let i = 0; i < data?.discountCodes.length; i++) {
          if (data?.discountCodes[i]?.paxType == paxType) {
            tempPromoPrice = data?.discountCodes[i]?.cabinPrice;
            if (promoPrice == 0) {
              promoPrice = tempPromoPrice;
              prevPromoPrice = promoPrice;
              specificPromo = data?.discountCodes[i]?.codeOfDiscount;
            } else if (prevPromoPrice > tempPromoPrice) {
              promoPrice = tempPromoPrice;
              prevPromoPrice = promoPrice;
              specificPromo = data?.discountCodes[i]?.codeOfDiscount;
            }
          }
        }
      }
    }

    if (data?.discountCodes) {
      for (let i = 0; i < data?.discountCodes.length; i++) {
        if (data?.discountCodes[i].generic == true) {
          tempgenDiscountPrice = data?.discountCodes[i].cabinPrice;
          if (genDiscountPrice == 0) {
            genDiscountPrice = data?.discountCodes[i]?.cabinPrice;
            prevgenDiscountPrice = genDiscountPrice;
            genPromo = data?.discountCodes[i]?.codeOfDiscount;
          } else if (prevgenDiscountPrice > tempgenDiscountPrice) {
            genDiscountPrice = tempgenDiscountPrice;
            prevgenDiscountPrice = genDiscountPrice;
            genPromo = data?.discountCodes[i]?.codeOfDiscount;
          }
        }
      }
    }

    if (genDiscountPrice > 0 && promoPrice > 0 && genDiscountPrice < promoPrice)
      appliedPromo = genPromo;
    if (genDiscountPrice > 0 && promoPrice > 0 && genDiscountPrice == promoPrice)
      appliedPromo = genPromo;
    else if (genDiscountPrice > 0 && promoPrice > 0 && genDiscountPrice > promoPrice)
      appliedPromo = specificPromo;
    else if (genDiscountPrice > 0 && promoPrice == 0) appliedPromo = genPromo;
    else if (genDiscountPrice == 0 && promoPrice > 0) appliedPromo = specificPromo;

    return appliedPromo;
  } else {
    if (paxType) {
      const pt = getQueryParam('paxType')?.replace('%2C', ',');
      if (pt) {
        const promotions = pt.split(',');
        for (const promo of promotions) {
          for (let i = 0; i < data?.discountCodes.length; i++) {
            if (data?.discountCodes[i]?.paxType == promo) {
              tempPromoPrice = data?.discountCodes[i]?.cabinPrice;
              if (promoPrice == 0) {
                promoPrice = tempPromoPrice;
                prevPromoPrice = promoPrice;
                specificPromo = data?.discountCodes[i]?.codeOfDiscount;
              } else if (prevPromoPrice > tempPromoPrice) {
                promoPrice = tempPromoPrice;
                prevPromoPrice = promoPrice;
                specificPromo = data?.discountCodes[i]?.codeOfDiscount;
              }
            }
          }
        }
        appliedPromo = specificPromo;
      }
    } else if (data?.discountCodes) {
      for (let i = 0; i < data?.discountCodes.length; i++) {
        if (data?.discountCodes[i].generic == true) {
          tempgenDiscountPrice = data?.discountCodes[i].cabinPrice;
          if (genDiscountPrice == 0) {
            genDiscountPrice = data?.discountCodes[i]?.cabinPrice;
            prevgenDiscountPrice = genDiscountPrice;
            genPromo = data?.discountCodes[i]?.codeOfDiscount;
          } else if (prevgenDiscountPrice > tempgenDiscountPrice) {
            genDiscountPrice = tempgenDiscountPrice;
            prevgenDiscountPrice = genDiscountPrice;
            genPromo = data?.discountCodes[i]?.codeOfDiscount;
          }
        }
      }
      appliedPromo = genPromo;
    }
    return appliedPromo;
  }
};

const getPromotionName = (data: any, state: RootState) => {
  const paxType = getPaxType(state?.search?.params?.filters, data);
  let appliedPromo = '';
  let tempPromoPrice = 0;
  let promoPrice = 0;
  let prevPromoPrice = 0;
  let specificPromo = '';
  let genDiscountPrice = 0;
  let prevgenDiscountPrice = 0;
  let tempgenDiscountPrice = 0;
  let genPromo = '';
  if (!isBooking()) {
    if (paxType) {
      const pt = getQueryParam('paxType')?.replace('%2C', ',');
      if (pt) {
        const promotions = pt.split(',');
        for (const promo of promotions) {
          for (let i = 0; i < data?.discountCodes.length; i++) {
            if (data?.discountCodes[i]?.paxType == promo) {
              tempPromoPrice = data?.discountCodes[i]?.cabinPrice;
              if (promoPrice == 0) {
                promoPrice = tempPromoPrice;
                prevPromoPrice = promoPrice;
                specificPromo = data?.discountCodes[i]?.paxType;
              } else if (prevPromoPrice > tempPromoPrice) {
                promoPrice = tempPromoPrice;
                prevPromoPrice = promoPrice;
                specificPromo = data?.discountCodes[i]?.paxType;
              }
            }
          }
        }
      } else {
        return paxType;
      }
    }

    if (data?.discountCodes) {
      for (let i = 0; i < data?.discountCodes.length; i++) {
        if (data?.discountCodes[i].generic == true) {
          tempgenDiscountPrice = data?.discountCodes[i].cabinPrice;
          if (genDiscountPrice == 0) {
            genDiscountPrice = data?.discountCodes[i]?.cabinPrice;
            prevgenDiscountPrice = genDiscountPrice;
            genPromo = data?.discountCodes[i]?.paxType;
          } else if (prevgenDiscountPrice > tempgenDiscountPrice) {
            genDiscountPrice = tempgenDiscountPrice;
            prevgenDiscountPrice = genDiscountPrice;
            genPromo = data?.discountCodes[i]?.paxType;
          }
        }
      }
    }

    if (genDiscountPrice > 0 && promoPrice > 0 && genDiscountPrice < promoPrice)
      appliedPromo = genPromo;
    if (genDiscountPrice > 0 && promoPrice > 0 && genDiscountPrice == promoPrice)
      appliedPromo = genPromo;
    else if (genDiscountPrice > 0 && promoPrice > 0 && genDiscountPrice > promoPrice)
      appliedPromo = specificPromo;
    else if (genDiscountPrice > 0 && promoPrice == 0) appliedPromo = genPromo;
    else if (genDiscountPrice == 0 && promoPrice > 0) appliedPromo = specificPromo;

    return appliedPromo;
  } else {
    if (paxType) {
      const pt = getQueryParam('paxType')?.replace('%2C', ',');
      if (pt) {
        const promotions = pt.split(',');
        for (const promo of promotions) {
          for (let i = 0; i < data?.discountCodes.length; i++) {
            if (data?.discountCodes[i]?.paxType == promo) {
              tempPromoPrice = data?.discountCodes[i]?.cabinPrice;
              if (promoPrice == 0) {
                promoPrice = tempPromoPrice;
                prevPromoPrice = promoPrice;
                specificPromo = data?.discountCodes[i]?.paxType;
              } else if (prevPromoPrice > tempPromoPrice) {
                promoPrice = tempPromoPrice;
                prevPromoPrice = promoPrice;
                specificPromo = data?.discountCodes[i]?.paxType;
              }
            }
          }
        }
        appliedPromo = specificPromo;
      }
    } else if (data?.discountCodes) {
      for (let i = 0; i < data?.discountCodes.length; i++) {
        if (data?.discountCodes[i].generic == true) {
          tempgenDiscountPrice = data?.discountCodes[i].cabinPrice;
          if (genDiscountPrice == 0) {
            genDiscountPrice = data?.discountCodes[i]?.cabinPrice;
            prevgenDiscountPrice = genDiscountPrice;
            genPromo = data?.discountCodes[i]?.paxType;
          } else if (prevgenDiscountPrice > tempgenDiscountPrice) {
            genDiscountPrice = tempgenDiscountPrice;
            prevgenDiscountPrice = genDiscountPrice;
            genPromo = data?.discountCodes[i]?.paxType;
          }
        }
      }
      appliedPromo = genPromo;
    }
    return appliedPromo;
  }
};

function getPaxType(query: string, data: any): string | null {
  const virtualPageName = mscGet<string>('session', 'virtualPageName');
  if (!virtualPageName?.includes('search')) {
    const match = query.match(/\bpaxType:"([^"]+)"/);
    return match ? match[1] : '';
  } else
    return Array.isArray(data?.paxTypeTrue) && data?.paxTypeTrue.length > 0
      ? data.paxTypeTrue[0]
      : '';
}

const calculatePriceDeals = (data: any, state: RootState) => {
  const paxType = getPaxType(state?.search?.params?.filters, data);
  let price = 0;
  if (isDealsPage()) {
    if (data?.discountCodes) {
      for (let i = 0; i < data?.discountCodes?.length; i++) {
        if (data?.discountCodes[i].paxType == paxType) {
          price = data?.discountCodes[i].cabinPrice;
          return price;
        }
      }
    }
  }
  return price;
};

const calculateDiscount = (data: any, state: RootState) => {
  const price = data?.prices?.cabinPrice ? parseFloat(data?.prices?.cabinPrice) : 0;
  let genDiscountPrice = 0;
  let prevgenDiscountPrice = 0;
  let tempgenDiscountPrice = 0;
  const paxType = getPaxType(state?.search?.params?.filters, data);

  if (isDealsPage()) {
    if (data?.discountCodes) {
      for (let i = 0; i < data?.discountCodes?.length; i++) {
        if (data?.discountCodes[i].paxType == paxType) {
          tempgenDiscountPrice = data?.discountCodes[i].cabinPrice;
          if (genDiscountPrice == 0) {
            genDiscountPrice = data?.discountCodes[i].cabinPrice;
            prevgenDiscountPrice = genDiscountPrice;
          } else if (prevgenDiscountPrice > tempgenDiscountPrice) {
            genDiscountPrice = tempgenDiscountPrice;
            prevgenDiscountPrice = genDiscountPrice;
          }
        }
      }
    }
  } else if (data?.discountCodes) {
    for (let i = 0; i < data?.discountCodes?.length; i++) {
      if (data?.discountCodes[i].generic == true) {
        tempgenDiscountPrice = data?.discountCodes[i].cabinPrice;
        if (genDiscountPrice == 0) {
          genDiscountPrice = data?.discountCodes[i].cabinPrice;
          prevgenDiscountPrice = genDiscountPrice;
        } else if (prevgenDiscountPrice > tempgenDiscountPrice) {
          genDiscountPrice = tempgenDiscountPrice;
          prevgenDiscountPrice = genDiscountPrice;
        }
      }
    }
  }

  if (!isBooking()) {
    let tempPromoPrice = 0;
    let promoPrice = 0;
    let prevPromoPrice = 0;
    if (paxType) {
      const pt = getQueryParam('paxType')?.replace('%2C', ',');
      if (pt) {
        const promotions = pt.split(',');
        for (const promo of promotions) {
          for (let i = 0; i < data?.discountCodes.length; i++) {
            if (data?.discountCodes[i]?.paxType == promo) {
              tempPromoPrice = data?.discountCodes[i]?.cabinPrice;
              if (promoPrice == 0) {
                promoPrice = tempPromoPrice;
                prevPromoPrice = promoPrice;
              } else if (prevPromoPrice > tempPromoPrice) {
                promoPrice = tempPromoPrice;
                prevPromoPrice = promoPrice;
              }
            }
          }
        }
      } else {
        for (let i = 0; i < data?.discountCodes.length; i++) {
          if (data?.discountCodes[i]?.paxType == paxType) {
            tempPromoPrice = data?.discountCodes[i]?.cabinPrice;
            if (promoPrice == 0) {
              promoPrice = tempPromoPrice;
              prevPromoPrice = promoPrice;
            } else if (prevPromoPrice > tempPromoPrice) {
              promoPrice = tempPromoPrice;
              prevPromoPrice = promoPrice;
            }
          }
        }
      }

      if (promoPrice < genDiscountPrice && genDiscountPrice > 0 && promoPrice > 0)
        return price - promoPrice;
      else if (promoPrice > genDiscountPrice && genDiscountPrice == 0 && promoPrice > 0)
        return price - promoPrice;
      else if (genDiscountPrice > 0) return price - genDiscountPrice;
    } else if (genDiscountPrice > 0) {
      return price - genDiscountPrice;
    }

    //Check if there is strikeThrough
    if (data?.prices?.strikethroughPrice?.cabinPrice > 0) {
      return data?.prices?.strikethroughPrice?.cabinPrice - price;
    }
    return price - data?.pricesAfterDiscount?.cabinPrice;
  } else {
    let tempPromoPrice = 0;
    let promoPrice = 0;
    let prevPromoPrice = 0;
    if (paxType) {
      const pt = getQueryParam('paxType')?.replace('%2C', ',');
      if (pt) {
        const promotions = pt.split(',');
        for (const promo of promotions) {
          for (let i = 0; i < data?.discountCodes.length; i++) {
            if (data?.discountCodes[i]?.paxType == promo) {
              tempPromoPrice = data?.discountCodes[i]?.cabinPrice;
              if (promoPrice == 0) {
                promoPrice = tempPromoPrice;
                prevPromoPrice = promoPrice;
              } else if (prevPromoPrice > tempPromoPrice) {
                promoPrice = tempPromoPrice;
                prevPromoPrice = promoPrice;
              }
            }
          }
        }
      }
      return price - promoPrice;
    } else {
      if (genDiscountPrice > 0) return price - genDiscountPrice;
      //Check if there is strikeThrough
      if (data?.prices?.strikethroughPrice?.cabinPrice > 0) {
        return data?.prices?.strikethroughPrice?.cabinPrice - price;
      } else return price - data?.pricesAfterDiscount?.cabinPrice;
    }
  }
};

const calculateBFdiscount = (data: any, state: RootState) => {
  let striket = 0;
  let hotelServiceChargesTotal = 0;
  const bookingStepsStoreFirstCabinObj = state?.bookingStepsStore?.firstCabinSelections?.cabinObj;
  const bookingStepsStoreTotalInclusiveCabinPrice = state?.priceTypes?.priceTypes
    ? state?.priceTypes?.priceTypes[0]?.lowestPricedCategory?.totalAllInclusiveCabinPrice
    : null;
  if (bookingStepsStoreFirstCabinObj?.hotelServiceChargesTotal) {
    hotelServiceChargesTotal = parseFloat(bookingStepsStoreFirstCabinObj?.hotelServiceChargesTotal);
  }

  let strikethroughPrice = null;
  let priceOfCard = null;

  if (state?.bookingStepsStore?.selectedPriceType?.lowestPricedCategory?.strikethroughPrice) {
    strikethroughPrice =
      state?.bookingStepsStore?.selectedPriceType?.lowestPricedCategory?.strikethroughPrice;
    priceOfCard = bookingStepsStoreFirstCabinObj?.priceBeforeDiscount;
  } else if (state?.priceTypes?.priceTypes?.[0].lowestPricedCategory?.strikethroughPrice) {
    strikethroughPrice =
      state?.priceTypes?.priceTypes?.[0].lowestPricedCategory?.strikethroughPrice;
    priceOfCard =
      state?.priceTypes?.priceTypes?.[0].lowestPricedCategory?.totalCabinPrice?.afterDiscount;
    hotelServiceChargesTotal = parseFloat('0');
  }

  if (data?.bookingStepsStore?.firstCabinSelections?.cabinObj) {
    if (
      //case 1 -  bookingStepsStoreFirstCabinObj is populated with data
      parseFloat(bookingStepsStoreFirstCabinObj?.priceBeforeDiscount) > 0 &&
      parseFloat(bookingStepsStoreFirstCabinObj?.price) > 0 &&
      strikethroughPrice == null
    ) {
      return (
        Math.round(
          (parseFloat(bookingStepsStoreFirstCabinObj?.priceBeforeDiscount) -
            parseFloat(bookingStepsStoreFirstCabinObj?.price)) *
            100
        ) / 100 || 0
      );
    } else if (
      //case 2 -  bookingStepsStoreTotalInclusiveCabinPrice is populated with data
      bookingStepsStoreTotalInclusiveCabinPrice?.beforeDiscount &&
      parseFloat(bookingStepsStoreTotalInclusiveCabinPrice?.beforeDiscount) > 0 &&
      parseFloat(bookingStepsStoreTotalInclusiveCabinPrice.afterDiscount) > 0 &&
      strikethroughPrice == null
    ) {
      return (
        Math.round(
          (parseFloat(bookingStepsStoreTotalInclusiveCabinPrice.beforeDiscount) -
            parseFloat(bookingStepsStoreTotalInclusiveCabinPrice.afterDiscount)) *
            100
        ) / 100 || 0
      );
    } else if (strikethroughPrice) {
      //case 3 -  strikethroughPrice is populated
      return Math.round(
        2 * parseFloat(strikethroughPrice || '0') -
          hotelServiceChargesTotal -
          parseFloat(priceOfCard || '0')
      );
    }
  }

  if (!data?.bookingStepsStore?.prices) {
    //Strikethrough discount
    if (data?.bookingStepsStore?.selectedPriceType?.lowestPricedCategory?.discounts) {
      for (
        let i = 0;
        i < data?.bookingStepsStore?.selectedPriceType?.lowestPricedCategory?.discounts?.length;
        i++
      ) {
        striket += Number(
          data?.bookingStepsStore?.selectedPriceType?.lowestPricedCategory?.discounts[i]?.amount
        );
      }
    }

    return striket;
  } else {
    let discount = 0;
    for (
      let i = 0;
      i < data?.bookingStepsStore?.prices?.priceBreakdown[0]?.discounts?.length;
      i++
    ) {
      discount += Number(data?.bookingStepsStore?.prices?.priceBreakdown[0]?.discounts[i]?.amount);
    }
    return Math.ceil(discount * 100) / 100;
  }
};

export const getStorageIndex = (): number => {
  try {
    // Parse item_order and finalIndex safely
    const itemOrders =
      mscGet<Record<string, number>>('session', 'item_order') ?? ({} as Record<string, number>);
    const itemId = store.getState().cruiseData?.cruiseID || '';
    let storageIndex = Number(mscGet<number | string>('session', 'finalIndex') ?? 0); // Default to 0

    // Compute storageIndex
    if (itemId) {
      storageIndex = itemOrders[itemId] || storageIndex;
    }

    return storageIndex;
  } catch (error) {
    console.error('Error retrieving storageIndex:', error);
    return 0; // Fallback to a safe default value
  }
};

//get date length
function getDaysInclusive(start: string, end: string): string {
  if (!start || !end) return '';
  const startDate = new Date(`${start}T00:00:00Z`);
  const endDate = new Date(`${end}T00:00:00Z`);

  const diffDays = Math.floor(Math.abs(+endDate - +startDate) / (1000 * 60 * 60 * 24));

  return diffDays.toString(); // inclusive count
}
// Helper function to map item data
// This function standardizes the structure of item data used in different events (in the booking funnel flow)
let prevStepCoupon = '';
const mapItemDataBooking = (data: any, state: RootState) => {
  userState = state;
  const storageIndex = getStorageIndex();

  const itemLecelCouponVal = sessionStorage?.getItem('itemLevelCoupon');
  let couponVal = '';
  if (state?.bookingStepsStore?.eCouponCode?.code) {
    couponVal = state?.bookingStepsStore?.eCouponCode?.code;
  } else if (itemLecelCouponVal) {
    couponVal = itemLecelCouponVal;
  }

  const hasStrikethrough = sessionStorage?.getItem('hasStrikethrough');
  const bfStrikeThrough = data?.genericConfigs?.configs?.b2cCommerceConfig?.strikethrough[0]
    ?.enableStrikethroughBF
    ? 'yes'
    : 'no';
  let finalStrikethrough = 'no';
  if (hasStrikethrough == 'yes' && bfStrikeThrough == 'no') finalStrikethrough = hasStrikethrough;
  else if (calculateBFdiscount(data, state) > 0) finalStrikethrough = bfStrikeThrough;
  else if (hasStrikethrough == 'yes' && bfStrikeThrough == 'yes') finalStrikethrough = 'yes';

  //Check if promotion has been applied automatically
  if (couponVal == '' && finalStrikethrough == 'yes') {
    couponVal =
      'strk-' +
      (state?.bookingStepsStore?.selectedPriceType?.priceType?.toLowerCase()
        ? state?.bookingStepsStore?.selectedPriceType?.priceType?.toLowerCase()
        : '');

    sessionStorage?.setItem('cruiseLevelCoupon', couponVal);
  }

  if (finalStrikethrough == 'yes' && prevStepCoupon != '' && couponVal == '')
    couponVal = prevStepCoupon;

  prevStepCoupon = couponVal;

  const queryPriceType = getQueryParam('priceType')?.toLowerCase();

  const matchedPriceType =
    state?.priceTypes?.priceTypes &&
    getMatchingPriceType(state.priceTypes.priceTypes, queryPriceType);

  let paxType =
    matchedPriceType?.lowestPricedCategory?.passengerType ||
    state?.bookingStepsStore?.selectedPriceType?.lowestPricedCategory?.passengerType ||
    '';

  const discounts = state?.bookingStepsStore?.selectedPriceType?.lowestPricedCategory?.discounts;
  const selectedDiscounts = matchedPriceType?.lowestPricedCategory?.discounts;
  let promotionID = selectedDiscounts?.[0]?.code ?? discounts?.[0]?.code ?? '';

  if (
    (state?.bookingStepsStore?.prices?.booking?.passengerType &&
      state?.bookingStepsStore?.selectedPriceType?.lowestPricedCategory?.passengerType &&
      state?.bookingStepsStore?.selectedPriceType?.lowestPricedCategory?.passengerType !=
        state?.bookingStepsStore?.prices?.booking?.passengerType) ||
    (paxType === '' && state?.bookingStepsStore?.prices?.booking?.passengerType)
  ) {
    paxType = state?.bookingStepsStore?.prices?.booking?.passengerType;
    if (
      paxType &&
      state?.bookingStepsStore?.prices?.priceBreakdown &&
      state?.bookingStepsStore?.prices?.priceBreakdown[0]?.discounts
    ) {
      promotionID = calculatePromotion(state);
    }
  }

  const promoId = mscGet<string>('session', 'promId') ? mscGet<string>('session', 'promId') : '';
  if (promoId != promotionID && promoId != '' && promotionID != '' && paxType)
    mscSet('session', 'promId', promotionID);

  const itemListName = sessionStorage?.getItem('itemListName');

  return {
    item_id: store.getState().cruiseData?.cruiseID ? store.getState().cruiseData?.cruiseID : '',
    item_name: sessionStorage?.getItem('itemName')
      ? sessionStorage?.getItem('itemName')
      : store.getState().cruiseData?.name?.toLowerCase() ?? '',
    coupon: finalStrikethrough == 'yes' ? couponVal : '',
    discount: calculateBFdiscount(data, state),
    index: storageIndex,
    item_brand: state?.cruiseData?.brand?.toLowerCase()
      ? state?.cruiseData?.brand?.toLowerCase()
      : '',
    item_category: getQueryParam('isFlightIncluded') === 'true' ? 'cruiseflight' : 'cruiseonly',
    item_category2: constructItemCategory2(data),
    item_category3: getItemCategory3(state) || '',
    item_list_name: itemListName || '',
    item_variant: 'booking',
    price: getTotalPrice(state),
    quantity: 1,
    currency: state?.genericConfigs?.configs?.b2cCommerceConfig?.currencyCode?.toLowerCase()
      ? state?.genericConfigs?.configs?.b2cCommerceConfig?.currencyCode?.toLowerCase()
      : '',
    adults_number: (
      state?.occupancy?.occupancyFirstCabinGuests?.adults +
      (state?.occupancy?.occupancySecondCabinGuests?.needed
        ? state?.occupancy?.occupancySecondCabinGuests?.adults
        : 0)
    ).toString(),
    cabin_num: state?.occupancy?.occupancySecondCabinGuests?.needed ? '2' : '1',
    cruise_starting_date: state?.cruiseData.departureDate ? state?.cruiseData.departureDate : '',
    cruise_ending_date: state?.cruiseData.arrivalDate ? state?.cruiseData.arrivalDate : '',
    cruise_length: getDaysInclusive(
      state?.cruiseData?.departureDate || '',
      state?.cruiseData?.arrivalDate || ''
    ).toString(),
    cruise_title: state?.cruiseData.title?.toLowerCase()
      ? state?.cruiseData.title?.toLowerCase()
      : '',
    cruise_type:
      state?.cruiseData.departurePort === state?.cruiseData.arrivalPort ? 'round trip' : 'oneway',
    cruise_canvas: sessionStorage?.getItem('cruise_canvas')
      ? sessionStorage?.getItem('cruise_canvas')
      : store.getState().cruiseData.season?.toLowerCase() ?? '',
    cruise_promo_badge: state.cruiseData.ribbon ?? '',
    arrival_port: state?.cruiseData?.departurePort?.toLowerCase()
      ? state?.cruiseData?.departurePort?.toLowerCase()
      : '',
    departing_port: state?.cruiseData?.arrivalPort?.toLowerCase()
      ? state?.cruiseData?.arrivalPort?.toLowerCase()
      : '',
    fly_cruise_selected: state.bookingParams.isFlightIncluded ? 'yes' : 'no',
    fly_departure: state.bookingParams.departureairportcode
      ? state.bookingParams.departureairportcode
      : '',
    number_passengers: (
      (state?.occupancy?.occupancyFirstCabinGuests?.adults || 0) +
      (state?.occupancy?.occupancyFirstCabinGuests?.minor || 0) +
      (state?.occupancy?.occupancyFirstCabinGuests?.infants || 0) +
      (state?.occupancy?.occupancySecondCabinGuests?.needed
        ? (state?.occupancy?.occupancySecondCabinGuests?.adults || 0) +
          (state?.occupancy?.occupancySecondCabinGuests?.minor || 0) +
          (state?.occupancy?.occupancySecondCabinGuests?.infants || 0)
        : 0)
    ).toString(),
    cabin_connected:
      state?.occupancy?.occupancySecondCabinGuests?.needed &&
      state?.occupancy?.occupancySecondCabinGuests?.requestConnectedCabin
        ? 'yes'
        : 'no',
    cabin_accessible:
      state?.occupancy?.occupancyFirstCabinGuests?.cabinAccessible ||
      (state?.occupancy?.occupancySecondCabinGuests?.needed &&
        state?.occupancy?.occupancySecondCabinGuests?.cabinAccessible)
        ? 'yes'
        : 'no',
    price_name:
      state?.bookingStepsStore?.selectedPriceType?.lowestPricedCategory.priceDescription?.toLowerCase() ||
      matchedPriceType?.lowestPricedCategory.priceDescription?.toLowerCase() ||
      '',
    price_type:
      state?.bookingStepsStore?.selectedPriceType?.priceType?.toLowerCase() ||
      matchedPriceType?.priceType?.toLowerCase() ||
      '',
    price_code:
      state?.bookingStepsStore?.selectedPriceType?.lowestPricedCategory.priceCode?.toLowerCase() ||
      matchedPriceType?.lowestPricedCategory.priceCode?.toLowerCase() ||
      '',
    promotion_name: paxType,
    promotion_id: paxType ? promotionID : '',
    creative_name: paxType,
    creative_slot: paxType ? 'itinerary card' : '',
    pax_type: paxType,
    strikethrough: finalStrikethrough,
  };
};

const calculatePageValues = (): { pc: string; psc: string; pssc: string; vpn: string } => {
  const urlValue = window.location.pathname;
  const urlParts: string[] = urlValue.split('/');
  let index = 1;

  let pc = urlParts[index] ? urlParts[index] : '';
  if (pc == window.pageViewContent['page_language']) {
    index = 2;
    pc = urlParts[index] ? urlParts[index] : '';
  }
  let psc = urlParts[index + 1] ? urlParts[index + 1] : '';
  let pssc = urlParts[index + 2] ? urlParts[index + 2] : '';

  const pageViewContent = window.pageViewContent || {};

  const vpn =
    pageViewContent?.['page_category'] +
    (pageViewContent?.['page_sub_category'] ? '/' + pageViewContent?.['page_sub_category'] : '') +
    (pageViewContent?.['page_sub_subcategory']
      ? '/' + pageViewContent?.['page_sub_subcategory']
      : '');

  if (vpn === 'home') {
    pc = vpn;
    psc = '';
    pssc = '';
  }
  return { pc, psc, pssc, vpn };
};

// Before navigating to a new page, save the current page as 'prev_page'
// Utility function to save the previous page
const savePrevPage = () => {
  const urlValue = window.location.pathname;
  const urlParts = urlValue.split('/');
  let index = 1; // this is works for all env, not local

  let pc = urlParts[index] ? urlParts[index] : '';
  if (pc == window.pageViewContent['page_language']) {
    index = 2;
    pc = urlParts[index] ? urlParts[index] : '';
  }
  let psc = urlParts[index + 1] ? urlParts[index + 1] : '';
  let pssc = urlParts[index + 2] ? urlParts[index + 2] : '';
  const vpn = window.location.pathname ? window.location.pathname : '';

  if (vpn == 'home') {
    pc = vpn;
    psc = urlParts[index + 1] ? urlParts[index + 1] : '';
    pssc = urlParts[index + 2] ? urlParts[index + 2] : '';
  }

  let currentPage = pc + (psc ? '/' + psc : '') + (pssc ? '/' + pssc : '');
  let prevPage = mscGet<string>('session', 'prev_page');
  const prevPage2 = mscGet<string>('session', 'prev_page2');

  // Save page_category and page_subcategory to session storage
  mscSet('session', 'pre_pc', pc);
  mscSet('session', 'pre_psc', psc);

  if (window?.pageViewContent?.['page_category'] !== 'booking') {
    const { pc, psc, pssc } = calculatePageValues();
    currentPage = pc + (psc ? '/' + psc : '') + (pssc ? '/' + pssc : '');
    prevPage = mscGet<string>('session', 'prev_page');
  }

  const prevPc = mscGet<string>('session', 'pre_pc');
  const prevPsc = mscGet<string>('session', 'pre_psc');

  // Handle special conditions based on page_category and subcategory
  let cl1, cl3, cl15;
  if (prevPc === 'booking' && prevPsc === '') {
    mscSet('session', 'prev_page', 'booking');
    if (
      currentPage == 'booking' &&
      prevPage != 'booking' &&
      mscGet<string>('session', 'prev_page') == 'booking' &&
      prevPsc === ''
    ) {
      mscSet('session', 'prev_page', mscGet<string>('session', 'prev_page2') || '');
      if (userState?.bookingStepsStore?.currentStep?.name == '') {
        Object.values(window.containerMap).forEach((value) => {
          const prefix = value.split('_')[0]; // Extract the prefix
          if (prefix == '2') cl1 = value;
          if (prefix == '3') cl3 = value;
          if (prefix == '15') cl15 = value;
        });

        mscSet('session', 'prev_page', 'booking');
        mscSet('session', 'lastClickedContainerId', cl1 || '2_pck_cruise');
        window.pageViewContent['container_list'] =
          cl3 || '3_cbc_cabin 1 / cabin 2' + ';' + cl15 || '15_sm_notassignedname';
      }
    }
  } else if (window?.pageViewContent?.['page_category'] === 'booking' && psc === '' && prevPage2) {
    mscSet('session', 'prev_page', prevPage2);
  } else if (currentPage !== prevPage) {
    mscSet('session', 'prev_page2', currentPage);
    mscSet('session', 'prev_page', currentPage);
  }
};

// Override history.pushState and history.replaceState to track navigation on booking funnel (SPA Application)

const originalPushState = history.pushState;
const originalReplaceState = history.replaceState;

// Wrap pushState to save previous page before navigation
history.pushState = function (...args) {
  originalPushState.apply(history, args);
};

// Wrap replaceState to save previous page before navigation
history.replaceState = function (...args) {
  originalReplaceState.apply(history, args);
};

const hasInsuranceStepInFunnel = (state: RootState): boolean =>
  Boolean(state?.bookingStepsStore?.hasInsuranceStepConfigured);

const normalizeInsuranceName = (value: unknown): string =>
  typeof value === 'string' ? value.trim().toLowerCase() : '';

const joinInsuranceNames = (values: unknown[]): string =>
  Array.from(new Set(values.map(normalizeInsuranceName).filter(Boolean))).join(':');

const getInsuranceAvailableValue = (state: RootState): string => {
  const insuranceProducts = state?.bookingStepsStore?.insuranceProducts ?? [];
  const insuranceLabels = state?.bookingStepsStore?.insuranceLabels ?? [];

  const unavailableInsuranceCodes = new Set(
    insuranceProducts
      .filter((insurance: any) => insurance?.isAvailable === false)
      .map((insurance: any) => insurance?.code?.toString().toLowerCase())
      .filter(Boolean)
  );

  const availableInsuranceLabels = insuranceLabels
    .filter(
      (insurance: any) =>
        !unavailableInsuranceCodes.has(insurance?.code?.toString().toLowerCase() ?? '')
    )
    .map((insurance: any) => insurance?.description);

  if (availableInsuranceLabels.length > 0) {
    return joinInsuranceNames(availableInsuranceLabels);
  }

  const availableInsuranceProducts = insuranceProducts
    .filter((insurance: any) => insurance?.isAvailable !== false)
    .map((insurance: any) => insurance?.description);

  return joinInsuranceNames(availableInsuranceProducts);
};

const getInsuranceSelectedValue = (state: RootState): string =>
  joinInsuranceNames(
    (state?.bookingStepsStore?.selectedInsurance ?? []).map(
      (insurance: any) => insurance?.description
    )
  );

const isInsuranceOfferedOnPaymentStep = (state: RootState): boolean => {
  if (hasInsuranceStepInFunnel(state)) return false;
  if (!state?.bookingStepsStore?.hasInsurance) return false;

  return getInsuranceAvailableValue(state) !== '';
};

const hasDedicatedInsuranceStepAvailable = (state: RootState): boolean =>
  hasInsuranceStepInFunnel(state) && getInsuranceAvailableValue(state) !== '';

const getPaymentStepNumber = (state: RootState): string =>
  hasDedicatedInsuranceStepAvailable(state) ? '10' : '9';

const getPostPaymentStepNumber = (state: RootState): string =>
  hasDedicatedInsuranceStepAvailable(state) ? '11' : '10';

const getInsuranceStepNumber = (state: RootState): string => {
  const paymentStepNumber = Number.parseInt(getPaymentStepNumber(state), 10);
  return Number.isFinite(paymentStepNumber) ? Math.max(paymentStepNumber - 1, 1).toString() : '9';
};

const getInsuranceAttributesForInsuranceStep = (state: RootState) => {
  const insuranceAvailable = getInsuranceAvailableValue(state);
  return insuranceAvailable ? { insurance_available: insuranceAvailable } : {};
};

const getInsuranceAttributesForPaymentStep = (state: RootState) => {
  if (hasDedicatedInsuranceStepAvailable(state)) {
    return { insurance_selected: getInsuranceSelectedValue(state) };
  }

  if (isInsuranceOfferedOnPaymentStep(state)) {
    return { insurance_available: getInsuranceAvailableValue(state) };
  }

  return {};
};

const getInsuranceAttributesForPostInsuranceSteps = (state: RootState) => {
  const insuranceSelected = getInsuranceSelectedValue(state);
  const shouldIncludeInsuranceSelected =
    hasDedicatedInsuranceStepAvailable(state) ||
    isInsuranceOfferedOnPaymentStep(state) ||
    insuranceSelected !== '';

  if (!shouldIncludeInsuranceSelected) {
    return {};
  }

  return { insurance_selected: insuranceSelected };
};

const toAmount = (value: unknown): number => {
  const amount = Number(value);
  return Number.isFinite(amount) ? amount : 0;
};

const getCabinPriceAmount = (price?: { afterDiscount?: unknown; beforeDiscount?: unknown }) => {
  const afterDiscountAmount = toAmount(price?.afterDiscount);
  if (afterDiscountAmount > 0) return afterDiscountAmount;

  const beforeDiscountAmount = toAmount(price?.beforeDiscount);
  return beforeDiscountAmount > 0 ? beforeDiscountAmount : 0;
};

const getInsuranceAmount = (insurance: any): number => {
  const bookingAmount = getCabinPriceAmount(insurance?.prices?.booking?.total);
  if (bookingAmount > 0) return bookingAmount;

  const passengersAmount = (insurance?.prices?.passengers ?? []).reduce(
    (sum: number, passenger: any) => sum + getCabinPriceAmount(passenger?.total),
    0
  );
  if (passengersAmount > 0) return passengersAmount;

  const directAmount = getCabinPriceAmount(insurance?.allInclusivePrice);
  if (directAmount > 0) return directAmount;

  return (insurance?.perPassenger ?? []).reduce(
    (sum: number, passenger: any) => sum + getCabinPriceAmount(passenger?.allInclusivePrice),
    0
  );
};

const getSelectedInsuranceTotal = (state: RootState): number =>
  (state?.bookingStepsStore?.selectedInsurance ?? []).reduce(
    (sum, insurance) => sum + getInsuranceAmount(insurance),
    0
  );

const hasMandatoryInsuranceSelected = (state: RootState): boolean =>
  (state?.bookingStepsStore?.selectedInsurance ?? []).some((insurance: any) =>
    (insurance?.groups ?? []).some((group: any) => Boolean(group?.insuranceProduct?.isMandatory))
  );

const getMandatoryInsuranceTotal = (state: RootState): number => {
  const firstCabinMandatoryInsurance =
    state?.bookingStepsStore?.firstCabinSelections?.cabinObj?.insurance ?? [];
  const secondCabinMandatoryInsurance = state?.occupancy?.occupancySecondCabinGuests?.needed
    ? state?.bookingStepsStore?.secondCabinSelections?.cabinObj?.insurance ?? []
    : [];

  const selectedInsuranceCodes = new Set(
    (state?.bookingStepsStore?.selectedInsurance ?? [])
      .map((insurance) => insurance?.code?.toString().toLowerCase())
      .filter(Boolean)
  );

  return [...firstCabinMandatoryInsurance, ...secondCabinMandatoryInsurance].reduce(
    (sum, insurance) => {
      const insuranceCode = insurance?.allInclusiveCode?.toString().toLowerCase();
      if (insuranceCode && selectedInsuranceCodes.has(insuranceCode)) {
        return sum;
      }
      return sum + getInsuranceAmount(insurance);
    },
    0
  );
};

const getInsuranceTotalForValue = (state: RootState): number => {
  const selectedInsuranceTotal = getSelectedInsuranceTotal(state);
  const mandatoryInsuranceTotal = getMandatoryInsuranceTotal(state);

  if (selectedInsuranceTotal === 0) {
    return mandatoryInsuranceTotal;
  }

  if (hasMandatoryInsuranceSelected(state)) {
    return selectedInsuranceTotal;
  }

  return selectedInsuranceTotal + mandatoryInsuranceTotal;
};

////////total value
const getTotalPrice = (state: RootState) => {
  if (
    state?.bookingStepsStore?.prices &&
    state?.bookingStepsStore?.prices?.priceBreakdown?.length > 0 &&
    (state?.bookingStepsStore?.currentStep?.name === 'checkout' ||
      state?.bookingStepsStore?.currentStep?.name === 'insurance')
  ) {
    return parseFloat(state?.bookingStepsStore?.prices?.booking?.total?.afterDiscount ?? '0');
  }

  const selectedPriceType = state?.bookingStepsStore?.selectedPriceType;
  //calculate price after discount
  let totalPriceValue = parseFloat(
    state?.bookingStepsStore?.firstCabinSelections?.cabinObj?.price ||
      state?.priceTypes?.priceTypes?.[0]?.lowestPricedCategory?.totalCabinPrice?.afterDiscount ||
      '0'
  );
  state?.bookingStepsStore?.firstCabinSelections?.selectedExtraServices?.forEach((service) => {
    if (service?.grossPrice) {
      totalPriceValue += parseFloat(service?.grossPrice);
    }
  });
  totalPriceValue += getInsuranceTotalForValue(state);

  if (state?.occupancy?.occupancySecondCabinGuests?.needed) {
    totalPriceValue += parseFloat(state?.bookingStepsStore?.secondCabinSelections?.cabinObj?.price);
    state?.bookingStepsStore?.secondCabinSelections?.selectedExtraServices?.forEach((service) => {
      if (service?.grossPrice) {
        totalPriceValue += parseFloat(service?.grossPrice);
      }
    });
  }

  const initialFlight = selectedPriceType
    ? state?.bookingStepsStore?.selectedPriceType?.lowestPricedCategory?.items?.find(
        (item) => item?.typeCode === 'AIR'
      )
    : null;
  if (state?.flightData?.selectedAirfare && initialFlight) {
    if (
      Number(state?.flightData?.selectedAirfare?.price?.total) - Number(initialFlight?.grossPrice) >
      0
    ) {
      totalPriceValue +=
        Number(state?.flightData?.selectedAirfare?.price?.total) -
        Number(initialFlight?.grossPrice);
    }
  }

  //calculate service charges
  let hotelServiceChargesTotal = 0;
  if (state?.bookingStepsStore?.firstCabinSelections?.cabinObj?.hotelServiceChargesTotal) {
    hotelServiceChargesTotal = parseFloat(
      state?.bookingStepsStore?.firstCabinSelections?.cabinObj?.hotelServiceChargesTotal
    );
  }
  if (state?.priceTypes?.priceTypes?.[0]?.lowestPricedCategory?.serviceCharges?.[0]?.amount?.total)
    hotelServiceChargesTotal = parseFloat(
      state?.priceTypes?.priceTypes?.[0]?.lowestPricedCategory?.serviceCharges?.[0]?.amount?.total
    );
  if (sessionStorage?.getItem('HotelFeeTotal')) {
    hotelServiceChargesTotal = parseFloat(sessionStorage?.getItem('HotelFeeTotal') || '0');
  }

  if (state?.bookingStepsStore?.firstCabinSelections?.cabinObj?.hotelServiceChargesTotal) {
    totalPriceValue += parseFloat(
      state?.bookingStepsStore?.firstCabinSelections?.cabinObj?.hotelServiceChargesTotal
    );

    return parseFloat(totalPriceValue?.toString());
  }

  //package preselected case
  if (state?.priceTypes?.priceTypes?.length === 1 && totalPriceValue === 0) {
    const { pricesPerPassenger, serviceCharges: selectedServiceCharges } =
      selectedPriceType?.lowestPricedCategory ?? {};
    const { allInclusive } = pricesPerPassenger?.[0] ?? {};

    const priceCurrent =
      selectedServiceCharges && selectedServiceCharges?.length > 0
        ? Number(selectedServiceCharges?.[0]?.amount?.perAdult) +
          Number(allInclusive?.afterDiscount)
        : allInclusive?.afterDiscount ?? '';

    totalPriceValue += Number(priceCurrent);
  }

  return parseFloat((totalPriceValue + hotelServiceChargesTotal)?.toString());
};

////////
//Analytics #1 page_view
const trackPageViewEpic = createAnalyticsEpic(
  trackPageView.type,
  () => {
    window.didomiOnReady = window.didomiOnReady || [];
    console.log('Didomi Wrapper trackPageView - About to push callback');
    window.didomiOnReady.push(() => {
      console.log('Didomi Wrapper trackPageViewEpic called - Didomi is ready');
      console.log('didomiVendorsEnabled =', window.didomiState?.didomiVendorsEnabled);
      window.adobeDataLayer?.push({ ecommerce: null });
      console.log('Custom event trackPageView ecommerce null pushed into the data layer');
    });
    const urlValue = window.location.pathname;
    const urlParts: string[] = urlValue.split('/');
    let index = 1;

    let pc = urlParts[index] ? urlParts[index] : '';
    if (pc == window.pageViewContent['page_language']) {
      index = 2;
      pc = urlParts[index] ? urlParts[index] : '';
    }
    const psc = urlParts[index + 1] ? urlParts[index + 1] : '';
    const pssc = urlParts[index + 2] ? urlParts[index + 2] : '';

    const virtualPageName = pc + (psc ? '/' + psc : '') + (pssc ? '/' + pssc : '');
    mscSet('session', 'virtualPageName', virtualPageName);

    // Use `event.composedPath()` to get all event path elements

    const storedPath = mscGet<EventTarget[]>('session', 'lastClickedPath');
    const pathArray = storedPath ? storedPath : []; // Traverse event path to find the first ancestor that had event.target
    let containerElement: HTMLElement | null = null;

    for (const element of pathArray) {
      if (element instanceof HTMLElement && element.hasAttribute('data-container-hash')) {
        containerElement = element;
        break; // Stop at the first matching ancestor
      }
    }

    if (containerElement) {
      const containerIdHash = containerElement.getAttribute('data-container-hash');

      if (containerIdHash && window.containerMap) {
        const myHashMap = window.containerMap as { [key: string]: string };
        const containerRef = myHashMap[containerIdHash];

        mscSet('session', 'lastClickedContainerId', containerRef || '');
      } else {
        mscSet('session', 'lastClickedContainerId', '');
      }
    }

    const originalContainerListString = window?.pageViewContent?.['container_list'] ?? '';
    const hiddenContainers: Map<string, string> = new Map();
    document
      .querySelectorAll('[data-container-hash], [data-react-component]')
      .forEach((element) => {
        const containerHash = element?.getAttribute('data-container-hash') ?? '';
        const correspondingValue = window?.containerMap?.[containerHash] ?? '';
        if (containerHash && correspondingValue) {
          hiddenContainers.set(
            containerHash.trim().toLowerCase(),
            correspondingValue.trim().toLowerCase()
          );
        }
      });
    const lastClickedContainerId = mscGet<string>(
      'session',
      'lastClickedContainerId'
    )?.toLowerCase();
    let container_list = '';
    let cl3 = '',
      cl10 = '',
      cl11 = '',
      cl2 = '',
      cl14 = '',
      cl15 = '';

    // Step 1: Extract cl10 and cl11 the container map values
    Object.values(window.containerMap).forEach((value) => {
      const prefix = value.split('_')[0]; // Extract the prefix
      if (prefix === '10') cl10 = value;
      if (prefix === '11') cl11 = value;
      if (prefix === '3') cl3 = value;
      if (prefix === '2') cl2 = value;
      if (prefix === '14') cl14 = value;
      if (prefix === '15') cl15 = value;
    });

    if (
      window.location.href.includes('thankyou?') ||
      mscGet<string>('session', 'lastClickedContainerId')?.includes('13_mp')
    ) {
      // Start a fresh container list with only cl14 and cl15 if they exist
      const containerItems: string[] = [];

      if (cl14) containerItems.push(cl14);

      // Re-index prefixes to be 1-based and sequential
      const renumbered = containerItems.map((item: string, index: number) => {
        const parts = item.split('_');
        parts[0] = (index + 1).toString();
        return parts.join('_');
      });

      container_list = renumbered.join(';');
    }

    // Step 2: If URL includes 'cruise-summary', start with container_list
    if (window.location.href.includes('cruise-summary')) {
      // Get original container_list from pageViewContent
      const originalList = window?.pageViewContent?.['container_list'] ?? '';
      const containerItems = originalList ? originalList.split(';') : [];

      // Step 3: Insert cl10 after the first item, if it exists
      if (cl10) {
        containerItems.splice(1, 0, cl10);
      }

      // Step 4: Check air conditions and insert cl11
      const hasAir =
        getQueryParam('isFlightIncluded') === 'true' || getQueryParam('flyAndCruise') === 'hasAir';

      if (cl11 && hasAir) {
        // Insert after cl10 (which is at index 1)
        containerItems.splice(2, 0, cl11);
      }

      // Step 5: Re-index prefixes to be 1-based and sequential
      const renumbered = containerItems.map((item: string, index: number) => {
        const parts = item.split('_');
        parts[0] = (index + 1).toString();
        return parts.join('_');
      });

      container_list = renumbered.join(';');
    }

    if (
      window.location.href.includes('occupancy?') &&
      window?.pageViewContent?.['container_list']
    ) {
      const originalList = window.pageViewContent['container_list'] ?? '';
      const containerItems = originalList ? originalList.split(';') : [];

      // Condition 1: Add cl3 at position 1 if 'pkc_cruise' is present
      if (originalList.includes('pkc_cruise') && cl3) {
        containerItems.splice(1, 0, cl3);
        mscSet('session', 'prev_page', 'booking');
      }

      // Condition 2: Add cl2 at beginning and cl15 at end if '3_cbc_cabin 1 / cabin 2' is present
      if (originalList.includes('cbc_cabin 1 / cabin 2') && cl15 && cl2) {
        containerItems.unshift(cl2);
        mscSet('session', 'prev_page', mscGet<string>('session', 'started_at') || '');
      }

      if (mscGet<string>('session', 'lastClickedContainerId') == '2_pkc_cruise') {
        const index = containerItems.findIndex((item: string) => item.includes('pkc_cruise'));
        if (index !== -1) {
          containerItems.splice(index, 1);
        }
      }

      const renumbered = containerItems.map((item: string, index: number) => {
        const parts = item.split('_');
        parts[0] = (index + 1).toString();
        return parts.join('_');
      });

      container_list = renumbered.join(';');
    }

    if (
      lastClickedContainerId &&
      originalContainerListString.includes(lastClickedContainerId) &&
      lastClickedContainerId != Array.from(hiddenContainers.values()).slice(-1)[0] &&
      !window.location.href.includes('thankyou?')
    ) {
      // Extract the last two values from hiddenContainers
      const lastTwoContainers = Array.from(hiddenContainers.values()).slice(-2);

      if (lastTwoContainers.length > 0) {
        // Append the last two values to the original container list
        container_list = lastTwoContainers.join(';');
        window.pageViewContent['container_list'] = container_list;
      }
    }
    if (mscGet<string>('session', 'prev_page'))
      mscSet('session', 'started_at', mscGet<string>('session', 'prev_page') || '');

    const pageData = {
      //Booking
      page: {
        ...window.pageViewContent,
        ...(container_list ? { container_list } : {}), // Only add container_list if it's not empty
        country: getCountryCodeFromMetaTag().toLowerCase(),
        website_area: 'public area', ///to be fixed in future case
        page_category: pc,
        page_sub_category: psc,
        page_sub_subcategory: pssc,
        virtual_page_name: virtualPageName,
        ref_container: mscGet<string>('session', 'lastClickedContainerId')
          ? mscGet<string>('session', 'lastClickedContainerId')
          : 'noref',
        prev_page: mscGet<string>('session', 'prev_page')
          ? mscGet<string>('session', 'prev_page')
          : '',
      },
      user: getUserInfo(userState),
    };

    savePrevPage();
    return pageData;
  },
  'page_view'
);

// Define the mapping of card brand variations to parameters
const cardTypeMapping: Record<string, { synonyms: string[]; code: string }> = {
  visa: { synonyms: ['visa'], code: 'vis' },
  mastercard: {
    synonyms: ['MASTERCARD', 'mastercard', 'master card', 'master-card', 'MCI', 'mci'],
    code: 'eca',
  },
  amex: { synonyms: ['amex', 'american express'], code: 'amx' },
  dinersclub: { synonyms: ['diners club', 'diners'], code: 'din' },
  discover: { synonyms: ['discover'], code: 'dis' },
  jcb: { synonyms: ['jcb'], code: 'jcb' },
  maestro: { synonyms: ['maestro'], code: 'mau' },
  dankort: { synonyms: ['dankort'], code: 'dn' },
  cup: { synonyms: ['cup', 'china union pay'], code: 'cup' },
};

// Utility function to find the matching card parameter
const getCardParameter = (brand: string | undefined): string => {
  if (!brand) return '';
  const lowercasedBrand = brand.toLowerCase();

  // Find a matching card type based on synonyms
  for (const [_key, value] of Object.entries(cardTypeMapping)) {
    if (value.synonyms.some((synonym) => lowercasedBrand.includes(synonym))) {
      return value.code;
    }
  }

  return ''; // Return 'unknown' if no match is found
};

const calculateCoupon = (data: RootState) => {
  return 'strk-' + data?.bookingStepsStore?.selectedPriceType?.priceType?.toLowerCase();
};

const calculatePromotion = (data: RootState) => {
  let promotion = '';
  for (let i = 0; i < data?.bookingStepsStore?.prices?.priceBreakdown[0]?.discounts?.length; i++) {
    if (promotion == '')
      promotion = data?.bookingStepsStore?.prices?.priceBreakdown[0]?.discounts[i].code;
    else
      promotion =
        promotion + ':' + data?.bookingStepsStore?.prices?.priceBreakdown[0]?.discounts[i].code;
  }
  return promotion;
};
const cabinsTypeAvailable = (state: RootState) => {
  const cabinTypes = state?.cabinTypes?.cabinTypes || [];
  return cabinTypes
    .map((cabin: { lowestPricedCategory?: { cabinType?: string } }) => {
      const macroCategory = cabin?.lowestPricedCategory?.cabinType;
      return macroCategory && CabinTypeEnum[macroCategory as keyof typeof CabinTypeEnum]
        ? CabinTypeEnum[macroCategory as keyof typeof CabinTypeEnum].toLowerCase()
        : '';
    })
    .filter(Boolean) // Remove empty values
    .join(':');
};

//algolia object id
const algoliaObjectId = (state: any) => {
  return (
    state?.bookingParams?.cruiseID +
    '|' +
    state?.genericConfigs?.configs?.b2cCommerceConfig?.currencyCode +
    '|*|' +
    state?.genericConfigs?.configs?.b2cCommerceConfig?.countryCodeISO3 +
    '|*|' +
    state?.bookingParams?.infantOccupancy +
    '|0|' +
    state?.bookingParams?.childrenOccupancy +
    '|' +
    state?.bookingParams?.adultsOccupancy +
    '|' +
    state?.bookingParams?.categoryKey +
    '|' +
    state?.bookingParams?.priceCode
  );
};

// Analytics for add_payment_info event
const trackAddPaymentInfoEpic = createAnalyticsEpic(
  trackPaymentInfo.type, // The action type to listen for
  (data: any, state: RootState) => {
    const availablePaymentMethods = getPaymentMethods();
    const mapPaymentMethodToMopCode = (method: string): string => {
      switch (method) {
        case 'klarna':
          return 'kln';
        case 'applepay':
          return 'apl';
        case 'googlepay':
          return 'pay';
        case 'paypal':
          return 'pap';
        default:
          return method;
      }
    };
    const availablePaymentButtons = getAllowedPaymentButtons();
    const cardNetworks = getAllowedCardNetworks();
    const transformedCardNetworks = cardNetworks.map((network) => getCardParameter(network));
    const cardNetworksString = transformedCardNetworks.join(':');
    const availablePaymentButtonsString = availablePaymentButtons.join(':');
    const paymentMethodsString = availablePaymentMethods.join(':');
    const paymentMethodsExpanded = paymentMethodsString
      .replace('securefields', cardNetworksString)
      .replace('googleapplepay', availablePaymentButtonsString);
    const paymentMethods = paymentMethodsExpanded
      .split(':')
      .filter(Boolean)
      .map(mapPaymentMethodToMopCode)
      .join(':');
    window.didomiOnReady = window.didomiOnReady || [];
    console.log('Didomi Wrapper trackAddPaymentInfo - About to push callback');
    window.didomiOnReady.push(() => {
      console.log('Didomi Wrapper trackAddPaymentInfoEpic called - Didomi is ready');
      console.log('didomiVendorsEnabled =', window.didomiState?.didomiVendorsEnabled);
      window.adobeDataLayer?.push({ ecommerce: null });
      console.log('Custom event trackAddPaymentInfo ecommerce null pushed into the data layer');
    });
    const storageIndex = getStorageIndex();

    let prepaidItemLevelCoupon = '';
    if (calculatePromotion(data)) {
      const promotions = calculatePromotion(data).split(':');
      for (let i = 0; i < promotions.length; i++)
        if (prepaidItemLevelCoupon == '') prepaidItemLevelCoupon = 'shrx-' + promotions[i];
        else prepaidItemLevelCoupon = prepaidItemLevelCoupon + ':' + 'shrx-' + promotions[i];
    } else prepaidItemLevelCoupon = 'shrx-none';

    const extraServices =
      state?.bookingStepsStore?.firstCabinSelections?.selectedExtraServices || [];
    const extraServicesItems = extraServices.map((extraService) => ({
      ...mapItemDataBooking(extraService, state),
      discount: 0,
      strikethrough: 'no',
      coupon: prepaidItemLevelCoupon || '',
      index: storageIndex,
      item_id: extraService?.code?.toLowerCase() ? extraService?.code?.toLowerCase() : '',
      item_name: extraService?.description?.toLowerCase()
        ? extraService?.description?.toLowerCase()
        : '',
      item_category: 'prepaid',
      item_category2: extraService?.serviceType?.toLowerCase()
        ? extraService?.serviceType?.toLowerCase()
        : '',
      item_variant: 'prepaid',
      price: extraService?.grossPrice ? parseFloat(extraService?.grossPrice) : '',
      item_category4: retrieveExperience(
        state?.bookingStepsStore?.firstCabinSelections?.selectedCategory
      ),
      cabin_preference_selected:
        state?.bookingStepsStore?.firstCabinSelections?.selectedCabinPreference?.toLowerCase()
          ? state?.bookingStepsStore?.firstCabinSelections?.selectedCabinPreference?.toLowerCase()
          : '',
      cabin_type_detail:
        state?.bookingStepsStore?.firstCabinSelections?.selectedCabinType?.toLowerCase()
          ? state?.bookingStepsStore?.firstCabinSelections?.selectedCabinType?.toLowerCase()
          : '',
      package_selected:
        state?.bookingStepsStore?.selectedPriceType?.lowestPricedCategory.priceDescription?.toLowerCase()
          ? state?.bookingStepsStore?.selectedPriceType?.lowestPricedCategory.priceDescription?.toLowerCase()
          : '',
      voyagers_club:
        (state?.bookingStepsStore?.firstCabinSelections?.voyagersMembersInfo &&
          state?.bookingStepsStore?.firstCabinSelections?.voyagersMembersInfo?.length > 0) ||
        (state?.bookingStepsStore?.secondCabinSelections?.voyagersMembersInfo &&
          state?.bookingStepsStore?.secondCabinSelections?.voyagersMembersInfo?.length > 0)
          ? 'yes'
          : 'no',
    }));

    const hasStrikethrough = sessionStorage?.getItem('hasStrikethrough');
    const bfStrikeThrough = (data as any)?.genericConfigs?.configs?.b2cCommerceConfig
      ?.strikethrough[0]?.enableStrikethroughBF
      ? 'yes'
      : 'no';
    let finalStrikethrough = 'no';
    if (hasStrikethrough == 'yes' && bfStrikeThrough == 'no') finalStrikethrough = hasStrikethrough;
    else if (calculateBFdiscount(data, state) > 0) finalStrikethrough = bfStrikeThrough;

    const insuranceTotal = getSelectedInsuranceTotal(state);
    const paymentCruiseItemPrice = Math.max(getTotalPrice(state) - insuranceTotal, 0);

    return {
      event: 'add_payment_info',
      step_name: 'checkout',
      step_number: getPaymentStepNumber(state),
      algolia_object_id: algoliaObjectId(state),
      area_of_payment: 'booking funnel',
      gender:
        state?.bookingStepsStore?.firstCabinSelections?.passengersData[0]?.gender?.toLowerCase() ||
        '',
      age_group: getAgeGroup(
        state?.bookingStepsStore?.firstCabinSelections?.passengersData[0]?.dateOfBirth
      ),
      mop_available: paymentMethods || '',
      ecommerce: {
        currency: state?.genericConfigs?.configs?.b2cCommerceConfig?.currencyCode?.toLowerCase()
          ? state?.genericConfigs?.configs?.b2cCommerceConfig?.currencyCode?.toLowerCase()
          : '',
        value: getTotalPrice(state),
        items: [
          {
            ...mapItemDataBooking(data, state),
            price: paymentCruiseItemPrice,
            coupon:
              calculateCoupon(data) && finalStrikethrough == 'yes' ? calculateCoupon(data) : '',
            cabin_preference_selected:
              state?.bookingStepsStore?.firstCabinSelections?.selectedCabinPreference?.toLowerCase()
                ? state?.bookingStepsStore?.firstCabinSelections?.selectedCabinPreference?.toLowerCase()
                : '',
            cabin_type_detail:
              state?.bookingStepsStore?.firstCabinSelections?.selectedCabinType?.toLowerCase()
                ? state?.bookingStepsStore?.firstCabinSelections?.selectedCabinType?.toLowerCase()
                : '',
            package_selected:
              state?.bookingStepsStore?.selectedPriceType?.lowestPricedCategory.priceDescription?.toLowerCase()
                ? state?.bookingStepsStore?.selectedPriceType?.lowestPricedCategory.priceDescription?.toLowerCase()
                : '',
            item_category4: retrieveExperience(
              state?.bookingStepsStore?.firstCabinSelections?.selectedCategory
            ),
            ...getInsuranceAttributesForPaymentStep(state),
            voyagers_club:
              (state?.bookingStepsStore?.firstCabinSelections?.voyagersMembersInfo &&
                state?.bookingStepsStore?.firstCabinSelections?.voyagersMembersInfo?.length > 0) ||
              (state?.bookingStepsStore?.secondCabinSelections?.voyagersMembersInfo &&
                state?.bookingStepsStore?.secondCabinSelections?.voyagersMembersInfo?.length > 0)
                ? 'yes'
                : 'no',
          },
          ...extraServicesItems,
        ],
      },
    };
  },
  'add_payment_info'
);

const totalDiscountCalculation = (state: RootState, activePaymentMethod: string) => {
  if (activePaymentMethod == 'totaltravelfare') {
    return state?.bookingStepsStore?.prices?.booking?.total?.beforeDiscount &&
      state?.bookingStepsStore?.prices?.booking?.total?.afterDiscount
      ? Number(state?.bookingStepsStore?.prices?.booking?.total?.beforeDiscount) -
          Number(state?.bookingStepsStore?.prices?.booking?.total?.afterDiscount)
      : 0;
  } else {
    return state?.bookingStepsStore?.prices?.booking?.deposit?.beforeDiscount &&
      state?.bookingStepsStore?.prices?.booking?.deposit?.afterDiscount
      ? Number(state?.bookingStepsStore?.prices?.booking?.deposit?.beforeDiscount) -
          Number(state?.bookingStepsStore?.prices?.booking?.deposit?.afterDiscount)
      : 0;
  }
};

// Analytics for purchase event during checkout
const trackPurchaseEventEpic = createAnalyticsEpic(
  trackPurchase.type, // The action type to listen for
  (data, state: RootState) => {
    window.didomiOnReady = window.didomiOnReady || [];
    console.log('Didomi Wrapper trackPurchase - About to push callback');
    window.didomiOnReady.push(() => {
      console.log('Didomi Wrapper trackPurchaseEventEpic called - Didomi is ready');
      console.log('didomiVendorsEnabled =', window.didomiState?.didomiVendorsEnabled);
      window.adobeDataLayer?.push({ ecommerce: null });
      console.log('Custom event trackPurchase ecommerce null pushed into the data layer');
    });
    const storageIndex = getStorageIndex();
    let hotelServiceChargesTotal = 0;
    if (state?.bookingStepsStore?.firstCabinSelections?.cabinObj?.hotelServiceChargesTotal) {
      hotelServiceChargesTotal = parseFloat(
        state?.bookingStepsStore?.firstCabinSelections?.cabinObj?.hotelServiceChargesTotal
      );
    }
    // Find the active payment method
    const activePaymentMethod = state?.bookingStepsStore?.paymentMethods?.find(
      (method) => method?.active === true
    );

    const itemLecelCouponVal = sessionStorage?.getItem('itemLevelCoupon');
    let couponVal = '';
    if (state?.bookingStepsStore?.eCouponCode?.code) {
      couponVal = state?.bookingStepsStore?.eCouponCode?.code;
    } else if (itemLecelCouponVal) {
      couponVal = itemLecelCouponVal;
    }

    const pr =
      parseFloat(state?.bookingStepsStore?.firstCabinSelections?.cabinObj?.price) ||
      (state?.priceTypes?.priceTypes &&
        parseFloat(
          state?.priceTypes?.priceTypes[0]?.lowestPricedCategory?.totalAllInclusiveCabinPrice
            ?.afterDiscount
        ));

    let coupon = 0;
    if (state?.bookingStepsStore?.prices?.priceBreakdown[0]?.discounts) {
      for (
        let i = 0;
        i < state?.bookingStepsStore?.prices?.priceBreakdown[0]?.discounts.length;
        i++
      ) {
        if (state?.bookingStepsStore?.prices?.priceBreakdown[0]?.discounts[i].code == couponVal)
          coupon = Number(
            state?.bookingStepsStore?.prices?.priceBreakdown[0]?.discounts[i]?.amount
          );
      }
    }

    let finalPrice = (pr ?? 0) - coupon;

    let prepaidItemLevelCoupon = '';
    if (calculatePromotion(state)) {
      const promotions = calculatePromotion(state).split(':');
      for (let i = 0; i < promotions.length; i++)
        if (prepaidItemLevelCoupon == '') prepaidItemLevelCoupon = 'shrx-' + promotions[i];
        else prepaidItemLevelCoupon = prepaidItemLevelCoupon + ':' + 'shrx-' + promotions[i];
    } else prepaidItemLevelCoupon = 'shrx-none';

    const hasStrikethrough = sessionStorage?.getItem('hasStrikethrough');
    const bfStrikeThrough = (data as any)?.genericConfigs?.configs?.b2cCommerceConfig
      ?.strikethrough[0]?.enableStrikethroughBF
      ? 'yes'
      : 'no';
    let finalStrikethrough = 'no';
    if (hasStrikethrough == 'yes' && bfStrikeThrough == 'no') finalStrikethrough = hasStrikethrough;
    else if (calculateBFdiscount(data, state) > 0) finalStrikethrough = bfStrikeThrough;

    const extraServices =
      state?.bookingStepsStore?.firstCabinSelections?.selectedExtraServices || [];
    const extraServicesItems = extraServices.map((extraService) => ({
      ...mapItemDataBooking(extraService, state),
      discount: 0,
      strikethrough: 'no',
      coupon: prepaidItemLevelCoupon || '',
      index: storageIndex,
      item_id: extraService?.code?.toLowerCase() ? extraService?.code?.toLowerCase() : '',
      item_name: extraService?.description?.toLowerCase()
        ? extraService?.description?.toLowerCase()
        : '',
      item_category: 'prepaid',
      item_category2: extraService?.serviceType?.toLowerCase()
        ? extraService?.serviceType?.toLowerCase()
        : '',
      item_variant: 'prepaid',
      price: extraService?.grossPrice ? parseFloat(extraService?.grossPrice) : '',

      item_category4: retrieveExperience(
        state?.bookingStepsStore?.firstCabinSelections?.selectedCategory
      ),
      package_selected:
        state?.bookingStepsStore?.selectedPriceType?.lowestPricedCategory.priceDescription?.toLowerCase()
          ? state?.bookingStepsStore?.selectedPriceType?.lowestPricedCategory.priceDescription?.toLowerCase()
          : '',
      cabin_preference_selected:
        state?.bookingStepsStore?.firstCabinSelections?.selectedCabinPreference?.toLowerCase()
          ? state?.bookingStepsStore?.firstCabinSelections?.selectedCabinPreference?.toLowerCase()
          : '',
      cabin_type_detail:
        state?.bookingStepsStore?.firstCabinSelections?.selectedCabinType?.toLowerCase()
          ? state?.bookingStepsStore?.firstCabinSelections?.selectedCabinType?.toLowerCase()
          : '',
      voyagers_club:
        (state?.bookingStepsStore?.firstCabinSelections?.voyagersMembersInfo &&
          state?.bookingStepsStore?.firstCabinSelections?.voyagersMembersInfo?.length > 0) ||
        (state?.bookingStepsStore?.secondCabinSelections?.voyagersMembersInfo &&
          state?.bookingStepsStore?.secondCabinSelections?.voyagersMembersInfo?.length > 0)
          ? 'yes'
          : 'no',
    }));

    finalPrice += getSelectedInsuranceTotal(state);

    let pt;

    if (activePaymentMethod?.method === 'klarna') {
      pt = 'kln';
    } else if (activePaymentMethod?.method == 'googleapplepay') {
      pt = 'google pay';
    } else if (activePaymentMethod?.method == 'paypal') {
      pt = 'paypal';
    } else {
      pt =
        typeof activePaymentMethod?.method === 'string' &&
        (activePaymentMethod.type === 'totaltravelfare' || activePaymentMethod.type === 'deposit')
          ? getCardParameter((data as any)?.cardInfo?.brand) || ''
          : activePaymentMethod?.method || '';
    }

    return {
      event: 'purchase',
      area_of_payment: 'booking funnel',
      algolia_object_id: algoliaObjectId(state),
      step_name: 'thankyou page',
      step_number: getPostPaymentStepNumber(state),
      gender:
        state?.bookingStepsStore?.firstCabinSelections?.passengersData[0]?.gender?.toLowerCase() ||
        '',
      age_group: getAgeGroup(
        state?.bookingStepsStore?.firstCabinSelections?.passengersData[0]?.dateOfBirth
      ),
      booking_id:
        state?.bookingStepsStore?.bookingResult?.bookingNumber !== -1
          ? state?.bookingStepsStore?.bookingResult?.bookingNumber
          : '',
      transaction_type: state?.bookingStepsStore?.bookingResult?.bookingType
        ? state?.bookingStepsStore?.bookingResult?.bookingType?.toLowerCase() === 'hold'
          ? 'option'
          : state?.bookingStepsStore?.bookingResult?.bookingType?.toLowerCase()
        : '',
      payment_type: pt,
      transaction_email: state?.bookingStepsStore?.firstCabinSelections?.passengersData[0]?.email
        ? saltedHash(state?.bookingStepsStore?.firstCabinSelections?.passengersData[0]?.email)
        : '',
      ecommerce: {
        transaction_id: state?.bookingStepsStore?.paymentMethods[0]?.active
          ? state?.bookingStepsStore?.prices?.booking?.total?.transactionId
          : state?.bookingStepsStore?.prices?.booking?.deposit?.transactionId,
        value: getTotalPrice(state),
        currency: state?.genericConfigs?.configs?.b2cCommerceConfig?.currencyCode?.toLowerCase()
          ? state?.genericConfigs?.configs?.b2cCommerceConfig?.currencyCode?.toLowerCase()
          : '',
        coupon: state?.bookingStepsStore?.eCouponCode?.code
          ? state?.bookingStepsStore?.eCouponCode?.code
          : '',
        total_discount:
          Math.ceil(
            Number(totalDiscountCalculation(state, activePaymentMethod?.type || '')) * 100
          ) / 100 || 0,
        base_deposit_amount:
          activePaymentMethod?.type == 'deposit'
            ? Math.ceil(
                Number(state?.bookingStepsStore?.prices?.booking?.deposit?.afterDiscount) * 100
              ) / 100 || 0
            : 0,
        items: [
          {
            ...mapItemDataBooking(data, state),
            price: finalPrice + hotelServiceChargesTotal,
            cabin_type_available: cabinsTypeAvailable(state),
            coupon:
              calculateCoupon(state) && finalStrikethrough == 'yes' ? calculateCoupon(state) : '',
            discount:
              Math.ceil(
                Number(totalDiscountCalculation(state, activePaymentMethod?.type || '')) * 100
              ) / 100 || 0,
            cabin_preference_selected:
              state?.bookingStepsStore?.firstCabinSelections?.selectedCabinPreference?.toLowerCase()
                ? state?.bookingStepsStore?.firstCabinSelections?.selectedCabinPreference?.toLowerCase()
                : '',
            cabin_type_detail:
              state?.bookingStepsStore?.firstCabinSelections?.selectedCabinType?.toLowerCase()
                ? state?.bookingStepsStore?.firstCabinSelections?.selectedCabinType?.toLowerCase()
                : '',
            index: storageIndex,
            package_selected:
              state?.bookingStepsStore?.selectedPriceType?.lowestPricedCategory.priceDescription?.toLowerCase()
                ? state?.bookingStepsStore?.selectedPriceType?.lowestPricedCategory.priceDescription?.toLowerCase()
                : '',
            item_category4: retrieveExperience(
              state?.bookingStepsStore?.firstCabinSelections?.selectedCategory
            ),
            ...getInsuranceAttributesForPostInsuranceSteps(state),
            voyagers_club:
              (state?.bookingStepsStore?.firstCabinSelections?.voyagersMembersInfo &&
                state?.bookingStepsStore?.firstCabinSelections?.voyagersMembersInfo?.length > 0) ||
              (state?.bookingStepsStore?.secondCabinSelections?.voyagersMembersInfo &&
                state?.bookingStepsStore?.secondCabinSelections?.voyagersMembersInfo?.length > 0)
                ? 'yes'
                : 'no',
          },
          ...extraServicesItems,
        ],
      },
    };
  },
  'purchase'
);

// Analytics for hold_option event
const trackHoldOptionEventEpic = createAnalyticsEpic(
  trackHoldOptionEvent.type, // The action type to listen for
  (data, state: RootState) => {
    const { bookingStepsStore } = data as any;

    // Find the active payment method
    const activePaymentMethod = state?.bookingStepsStore?.paymentMethods?.find(
      (method) => method?.active === true
    );
    window.didomiOnReady = window.didomiOnReady || [];
    console.log('Didomi Wrapper trackHoldOption - About to push callback');
    window.didomiOnReady.push(() => {
      console.log('Didomi Wrapper trackHoldOptionEventEpic called - Didomi is ready');
      console.log('didomiVendorsEnabled =', window.didomiState?.didomiVendorsEnabled);
      window.adobeDataLayer?.push({ ecommerce: null });
      console.log('Custom event trackHoldOption ecommerce null pushed into the data layer');
    });
    const storageIndex = getStorageIndex();

    let hotelServiceChargesTotal = 0;
    if (state?.bookingStepsStore?.firstCabinSelections?.cabinObj?.hotelServiceChargesTotal) {
      hotelServiceChargesTotal = parseFloat(
        state?.bookingStepsStore?.firstCabinSelections?.cabinObj?.hotelServiceChargesTotal
      );
    }

    let prepaidItemLevelCoupon = '';
    if (calculatePromotion(state)) {
      const promotions = calculatePromotion(state).split(':');
      for (let i = 0; i < promotions.length; i++)
        if (prepaidItemLevelCoupon == '') prepaidItemLevelCoupon = 'shrx-' + promotions[i];
        else prepaidItemLevelCoupon = prepaidItemLevelCoupon + ':' + 'shrx-' + promotions[i];
    } else prepaidItemLevelCoupon = 'shrx-none';

    const itemLecelCouponVal = sessionStorage?.getItem('itemLevelCoupon');
    let couponVal = '';
    if (state?.bookingStepsStore?.eCouponCode?.code) {
      couponVal = state?.bookingStepsStore?.eCouponCode?.code;
    } else if (itemLecelCouponVal) {
      couponVal = itemLecelCouponVal;
    }

    const pr =
      parseFloat(state?.bookingStepsStore?.firstCabinSelections?.cabinObj?.price) ||
      (state?.priceTypes?.priceTypes &&
        parseFloat(
          state?.priceTypes?.priceTypes[0]?.lowestPricedCategory?.totalAllInclusiveCabinPrice
            ?.afterDiscount
        ));

    let coupon = 0;
    if (state?.bookingStepsStore?.prices?.priceBreakdown[0]?.discounts) {
      for (
        let i = 0;
        i < state?.bookingStepsStore?.prices?.priceBreakdown[0]?.discounts.length;
        i++
      ) {
        if (state?.bookingStepsStore?.prices?.priceBreakdown[0]?.discounts[i].code == couponVal)
          coupon = Number(
            state?.bookingStepsStore?.prices?.priceBreakdown[0]?.discounts[i]?.amount
          );
      }
    }

    let finalPrice = (pr ?? 0) - coupon;

    finalPrice += getSelectedInsuranceTotal(state);

    const hasStrikethrough = sessionStorage?.getItem('hasStrikethrough');
    const bfStrikeThrough = (data as any)?.genericConfigs?.configs?.b2cCommerceConfig
      ?.strikethrough[0]?.enableStrikethroughBF
      ? 'yes'
      : 'no';
    let finalStrikethrough = 'no';
    if (hasStrikethrough == 'yes' && bfStrikeThrough == 'no') finalStrikethrough = hasStrikethrough;
    else if (calculateBFdiscount(data, state) > 0) finalStrikethrough = bfStrikeThrough;

    const extraServices =
      state?.bookingStepsStore?.firstCabinSelections?.selectedExtraServices || [];
    const extraServicesItems = extraServices.map((extraService) => ({
      ...mapItemDataBooking(extraService, state),
      discount: 0,
      strikethrough: 'no',
      coupon: prepaidItemLevelCoupon || '',
      index: storageIndex,
      item_id: extraService?.code?.toLowerCase() ? extraService?.code?.toLowerCase() : '',
      item_name: extraService?.description?.toLowerCase()
        ? extraService?.description?.toLowerCase()
        : '',
      item_category: 'prepaid',
      item_category2: extraService?.serviceType?.toLowerCase()
        ? extraService?.serviceType?.toLowerCase()
        : '',
      item_variant: 'prepaid',
      price: extraService?.grossPrice ? parseFloat(extraService?.grossPrice) : '',

      item_category4: retrieveExperience(
        state?.bookingStepsStore?.firstCabinSelections?.selectedCategory
      ),
      package_selected:
        state?.bookingStepsStore?.selectedPriceType?.lowestPricedCategory.priceDescription?.toLowerCase()
          ? state?.bookingStepsStore?.selectedPriceType?.lowestPricedCategory.priceDescription?.toLowerCase()
          : '',
      cabin_preference_selected:
        state?.bookingStepsStore?.firstCabinSelections?.selectedCabinPreference?.toLowerCase()
          ? state?.bookingStepsStore?.firstCabinSelections?.selectedCabinPreference?.toLowerCase()
          : '',
      cabin_type_detail:
        state?.bookingStepsStore?.firstCabinSelections?.selectedCabinType?.toLowerCase()
          ? state?.bookingStepsStore?.firstCabinSelections?.selectedCabinType?.toLowerCase()
          : '',
      voyagers_club:
        (state?.bookingStepsStore?.firstCabinSelections?.voyagersMembersInfo &&
          state?.bookingStepsStore?.firstCabinSelections?.voyagersMembersInfo?.length > 0) ||
        (state?.bookingStepsStore?.secondCabinSelections?.voyagersMembersInfo &&
          state?.bookingStepsStore?.secondCabinSelections?.voyagersMembersInfo?.length > 0)
          ? 'yes'
          : 'no',
    }));

    const getCheckboxConsent = (namePattern: string): string => {
      const checkbox = Array.from(
        document.querySelectorAll<HTMLInputElement>('input[type="checkbox"][name]')
      ).find((input) => input.name.toLowerCase().includes(namePattern));
      return checkbox ? (checkbox.checked ? 'optin' : 'optout') : '';
    };

    const marketing_consent = getCheckboxConsent('marketingconsent');
    const profiling_consent = getCheckboxConsent('profilingconsent');

    return {
      event: 'hold_option',
      area_of_payment: 'booking funnel',
      algolia_object_id: algoliaObjectId(state),
      step_name: 'thankyou Page',
      step_number: getPostPaymentStepNumber(state),
      gender:
        state?.bookingStepsStore?.firstCabinSelections?.passengersData[0]?.gender?.toLowerCase() ||
        '',
      age_group: getAgeGroup(
        state?.bookingStepsStore?.firstCabinSelections?.passengersData[0]?.dateOfBirth
      ),
      booking_id:
        state?.bookingStepsStore?.bookingResult?.bookingNumber !== -1
          ? state?.bookingStepsStore?.bookingResult?.bookingNumber
          : '',
      marketing_consent: marketing_consent,
      ...(profiling_consent && { profiling_consent }),
      ecommerce: {
        transaction_id: activePaymentMethod
          ? bookingStepsStore?.prices?.booking?.total?.transactionId
          : bookingStepsStore?.prices?.booking?.deposit?.transactionId,
        transaction_type: state?.bookingStepsStore?.bookingResult?.bookingType
          ? state.bookingStepsStore.bookingResult.bookingType.toLowerCase() === 'hold'
            ? 'option'
            : state.bookingStepsStore.bookingResult.bookingType.toLowerCase()
          : '',
        value: getTotalPrice(state),
        currency: state?.genericConfigs?.configs?.b2cCommerceConfig?.currencyCode?.toLowerCase()
          ? state?.genericConfigs?.configs?.b2cCommerceConfig?.currencyCode?.toLowerCase()
          : '',
        coupon: bookingStepsStore?.eCouponCode?.code ? bookingStepsStore?.eCouponCode?.code : '',
        total_discount:
          Math.ceil(
            Number(totalDiscountCalculation(state, activePaymentMethod?.type || '')) * 100
          ) / 100 || 0,
        items: [
          {
            ...mapItemDataBooking(data, state),
            cabin_type_available: cabinsTypeAvailable(state),
            coupon:
              calculateCoupon(state) && finalStrikethrough == 'yes' ? calculateCoupon(state) : '',
            discount:
              Math.ceil(
                Number(totalDiscountCalculation(state, activePaymentMethod?.type || '')) * 100
              ) / 100 || 0,
            price: finalPrice + hotelServiceChargesTotal,
            cabin_preference_selected: getCabinPreferenceSelected(state, data as any),
            cabin_type_detail: getCabinDetail(state, data as any),
            package_selected:
              state?.bookingStepsStore?.selectedPriceType?.lowestPricedCategory.priceDescription?.toLowerCase()
                ? state?.bookingStepsStore?.selectedPriceType?.lowestPricedCategory.priceDescription?.toLowerCase()
                : '',
            item_category4: retrieveExperience(
              state?.bookingStepsStore?.firstCabinSelections?.selectedCategory
            ),
            voyagers_club:
              (state?.bookingStepsStore?.firstCabinSelections?.voyagersMembersInfo &&
                state?.bookingStepsStore?.firstCabinSelections?.voyagersMembersInfo?.length > 0) ||
              (state?.bookingStepsStore?.secondCabinSelections?.voyagersMembersInfo &&
                state?.bookingStepsStore?.secondCabinSelections?.voyagersMembersInfo?.length > 0)
                ? 'yes'
                : 'no',
          },
          ...extraServicesItems,
        ],
      },
    };
  },
  'hold_option'
);

// Analytics for purchase_failed event
const trackPurchaseFailedEventEpic = createAnalyticsEpic(
  trackPurchaseFailed.type, // The action type to listen for
  (data: any, state: RootState) => {
    // Find the active payment method
    const activePaymentMethod = state?.bookingStepsStore?.paymentMethods?.find(
      (method: any) => method?.active === true
    );

    const storageIndex = getStorageIndex();

    let hotelServiceChargesTotal = 0;
    if (state?.bookingStepsStore?.firstCabinSelections?.cabinObj?.hotelServiceChargesTotal) {
      hotelServiceChargesTotal = parseFloat(
        state?.bookingStepsStore?.firstCabinSelections?.cabinObj?.hotelServiceChargesTotal
      );
    }

    let prepaidItemLevelCoupon = '';
    if (calculatePromotion(state)) {
      const promotions = calculatePromotion(state).split(':');
      for (let i = 0; i < promotions.length; i++)
        if (prepaidItemLevelCoupon == '') prepaidItemLevelCoupon = 'shrx-' + promotions[i];
        else prepaidItemLevelCoupon = prepaidItemLevelCoupon + ':' + 'shrx-' + promotions[i];
    } else prepaidItemLevelCoupon = 'shrx-none';

    const hasStrikethrough = sessionStorage?.getItem('hasStrikethrough');
    const bfStrikeThrough = (data as any)?.genericConfigs?.configs?.b2cCommerceConfig
      ?.strikethrough[0]?.enableStrikethroughBF
      ? 'yes'
      : 'no';
    let finalStrikethrough = 'no';
    if (hasStrikethrough == 'yes' && bfStrikeThrough == 'no') finalStrikethrough = hasStrikethrough;
    else if (calculateBFdiscount(data, state) > 0) finalStrikethrough = bfStrikeThrough;

    const extraServices =
      state?.bookingStepsStore?.firstCabinSelections?.selectedExtraServices || [];
    const extraServicesItems = extraServices.map((extraService) => ({
      ...mapItemDataBooking(extraService, state),
      discount: 0,
      strikethrough: 'no',
      coupon: prepaidItemLevelCoupon || '',
      index: storageIndex,
      item_id: extraService?.code?.toLowerCase() ? extraService?.code?.toLowerCase() : '',
      item_name: extraService?.description?.toLowerCase()
        ? extraService?.description?.toLowerCase()
        : '',
      item_category: 'prepaid',
      item_category2: extraService?.serviceType?.toLowerCase()
        ? extraService?.serviceType?.toLowerCase()
        : '',
      item_variant: 'prepaid',
      price: extraService?.grossPrice ? parseFloat(extraService?.grossPrice) : '',

      item_category4: retrieveExperience(
        state?.bookingStepsStore?.firstCabinSelections?.selectedCategory
      ),
      package_selected:
        state?.bookingStepsStore?.selectedPriceType?.lowestPricedCategory.priceDescription?.toLowerCase()
          ? state?.bookingStepsStore?.selectedPriceType?.lowestPricedCategory.priceDescription?.toLowerCase()
          : '',
      cabin_preference_selected:
        state?.bookingStepsStore?.firstCabinSelections?.selectedCabinPreference?.toLowerCase()
          ? state?.bookingStepsStore?.firstCabinSelections?.selectedCabinPreference?.toLowerCase()
          : '',
      cabin_type_detail:
        state?.bookingStepsStore?.firstCabinSelections?.selectedCabinType?.toLowerCase()
          ? state?.bookingStepsStore?.firstCabinSelections?.selectedCabinType?.toLowerCase()
          : '',
      voyagers_club:
        (state?.bookingStepsStore?.firstCabinSelections?.voyagersMembersInfo &&
          state?.bookingStepsStore?.firstCabinSelections?.voyagersMembersInfo?.length > 0) ||
        (state?.bookingStepsStore?.secondCabinSelections?.voyagersMembersInfo &&
          state?.bookingStepsStore?.secondCabinSelections?.voyagersMembersInfo?.length > 0)
          ? 'yes'
          : 'no',
    }));

    window.didomiOnReady = window.didomiOnReady || [];
    console.log('Didomi Wrapper trackPurchaseFailed - About to push callback');
    window.didomiOnReady.push(() => {
      console.log('Didomi Wrapper trackPurchaseFailedEventEpic called - Didomi is ready');
      console.log('didomiVendorsEnabled =', window.didomiState?.didomiVendorsEnabled);
      window.adobeDataLayer?.push({ ecommerce: null });
      console.log('Custom event trackPurchaseFailed ecommerce null pushed into the data layer');
    });

    const itemLecelCouponVal = sessionStorage?.getItem('itemLevelCoupon');
    let couponVal = '';
    if (state?.bookingStepsStore?.eCouponCode?.code) {
      couponVal = state?.bookingStepsStore?.eCouponCode?.code;
    } else if (itemLecelCouponVal) {
      couponVal = itemLecelCouponVal;
    }

    const pr =
      parseFloat(state?.bookingStepsStore?.firstCabinSelections?.cabinObj?.price) ||
      (state?.priceTypes?.priceTypes &&
        parseFloat(
          state?.priceTypes?.priceTypes[0]?.lowestPricedCategory?.totalAllInclusiveCabinPrice
            ?.afterDiscount
        ));

    let coupon = 0;
    if (state?.bookingStepsStore?.prices?.priceBreakdown[0]?.discounts) {
      for (
        let i = 0;
        i < state?.bookingStepsStore?.prices?.priceBreakdown[0]?.discounts.length;
        i++
      ) {
        if (state?.bookingStepsStore?.prices?.priceBreakdown[0]?.discounts[i].code == couponVal)
          coupon = Number(
            state?.bookingStepsStore?.prices?.priceBreakdown[0]?.discounts[i]?.amount
          );
      }
    }

    let finalPrice = (pr ?? 0) - coupon;

    finalPrice += getSelectedInsuranceTotal(state);

    let pt;
    if (activePaymentMethod?.method == 'googleapplepay') pt = 'google pay';
    else if (activePaymentMethod?.method == 'paypal') pt = 'paypal';
    else
      pt =
        typeof activePaymentMethod?.method === 'string' &&
        (activePaymentMethod.type === 'totaltravelfare' || activePaymentMethod.type === 'deposit')
          ? getCardParameter((data as any)?.cardInfo?.brand) || ''
          : activePaymentMethod?.method || '';

    return {
      event: 'purchase_failed',
      area_of_payment: 'booking funnel',
      algolia_object_id: algoliaObjectId(state),
      step_name: 'thankyou page',
      step_number: getPostPaymentStepNumber(state),
      gender:
        state?.bookingStepsStore?.firstCabinSelections?.passengersData[0]?.gender?.toLowerCase() ||
        '',
      age_group: getAgeGroup(
        state?.bookingStepsStore?.firstCabinSelections?.passengersData[0]?.dateOfBirth
      ),
      booking_id:
        state?.bookingStepsStore?.bookingResult?.bookingNumber !== -1
          ? state?.bookingStepsStore?.bookingResult?.bookingNumber
          : '',
      transaction_type: state?.bookingStepsStore?.bookingResult?.bookingType
        ? state.bookingStepsStore.bookingResult.bookingType.toLowerCase() === 'hold'
          ? 'option'
          : state.bookingStepsStore.bookingResult.bookingType.toLowerCase()
        : '',
      payment_type: pt,
      ecommerce: {
        transaction_id: activePaymentMethod
          ? state?.bookingStepsStore?.prices?.booking?.total?.transactionId
          : !state?.bookingStepsStore?.prices?.booking?.total ||
            !state?.bookingStepsStore?.prices?.booking?.deposit
          ? state?.bookingStepsStore?.merchantIdBooking
          : state?.bookingStepsStore?.prices?.booking?.deposit?.transactionId,
        value: getTotalPrice(state),
        currency: state?.genericConfigs?.configs?.b2cCommerceConfig?.currencyCode?.toLowerCase()
          ? state?.genericConfigs?.configs?.b2cCommerceConfig?.currencyCode?.toLowerCase()
          : '',
        coupon: state?.bookingStepsStore?.eCouponCode?.code
          ? state?.bookingStepsStore?.eCouponCode?.code
          : '',
        total_discount:
          Math.ceil(
            Number(totalDiscountCalculation(state, activePaymentMethod?.type || '')) * 100
          ) / 100 || 0,
        base_deposit_amount:
          activePaymentMethod?.type == 'deposit'
            ? Math.ceil(
                Number(state?.bookingStepsStore?.prices?.booking?.deposit?.afterDiscount) * 100
              ) / 100 || 0
            : 0,
        items: [
          {
            ...mapItemDataBooking(data, state),
            cabin_type_available: cabinsTypeAvailable(state),
            coupon:
              calculateCoupon(state) && finalStrikethrough == 'yes' ? calculateCoupon(state) : '',
            discount:
              Math.ceil(
                Number(totalDiscountCalculation(state, activePaymentMethod?.type || '')) * 100
              ) / 100 || 0,
            price: finalPrice + hotelServiceChargesTotal,
            cabin_preference_selected: getCabinPreferenceSelected(state, data as any),
            cabin_type_detail: getCabinDetail(state, data as any),
            package_selected:
              state?.bookingStepsStore?.selectedPriceType?.lowestPricedCategory.priceDescription?.toLowerCase()
                ? state?.bookingStepsStore?.selectedPriceType?.lowestPricedCategory.priceDescription?.toLowerCase()
                : '',
            item_category4: retrieveExperience(
              state?.bookingStepsStore?.firstCabinSelections?.selectedCategory
            ),
            voyagers_club:
              (state?.bookingStepsStore?.firstCabinSelections?.voyagersMembersInfo &&
                state?.bookingStepsStore?.firstCabinSelections?.voyagersMembersInfo?.length > 0) ||
              (state?.bookingStepsStore?.secondCabinSelections?.voyagersMembersInfo &&
                state?.bookingStepsStore?.secondCabinSelections?.voyagersMembersInfo?.length > 0)
                ? 'yes'
                : 'no',
          },
          ...extraServicesItems,
        ],
      },
    };
  },
  'purchase_failed'
);

const trackPurchaseDuringCheckoutEpic = createAnalyticsEpic(
  trackPaymentFailed.type, // The action type to listen for
  (data: any, state: RootState) => {
    // Find the active payment method
    const activePaymentMethod = state.bookingStepsStore?.paymentMethods?.find(
      (method: any) => method?.active === true
    );

    const storageIndex = getStorageIndex();

    let prepaidItemLevelCoupon = '';
    if (calculatePromotion(state)) {
      const promotions = calculatePromotion(state).split(':');
      for (let i = 0; i < promotions.length; i++)
        if (prepaidItemLevelCoupon == '') prepaidItemLevelCoupon = 'shrx-' + promotions[i];
        else prepaidItemLevelCoupon = prepaidItemLevelCoupon + ':' + 'shrx-' + promotions[i];
    } else prepaidItemLevelCoupon = 'shrx-none';

    const extraServices =
      state?.bookingStepsStore?.firstCabinSelections?.selectedExtraServices || [];
    const extraServicesItems = extraServices.map((extraService) => ({
      ...mapItemDataBooking(extraService, state),
      discount: 0,
      strikethrough: 'no',
      coupon: prepaidItemLevelCoupon || '',
      index: storageIndex,
      item_id: extraService?.code?.toLowerCase() ? extraService?.code?.toLowerCase() : '',
      item_name: extraService?.description?.toLowerCase()
        ? extraService?.description?.toLowerCase()
        : '',
      item_category: 'prepaid',
      item_category2: extraService?.serviceType?.toLowerCase()
        ? extraService?.serviceType?.toLowerCase()
        : '',
      item_variant: 'prepaid',
      price: extraService?.grossPrice ? parseFloat(extraService?.grossPrice) : '',

      item_category4: retrieveExperience(
        state?.bookingStepsStore?.firstCabinSelections?.selectedCategory
      ),
      package_selected:
        state?.bookingStepsStore?.selectedPriceType?.lowestPricedCategory.priceDescription?.toLowerCase()
          ? state?.bookingStepsStore?.selectedPriceType?.lowestPricedCategory.priceDescription?.toLowerCase()
          : '',
      cabin_preference_selected:
        state?.bookingStepsStore?.firstCabinSelections?.selectedCabinPreference?.toLowerCase()
          ? state?.bookingStepsStore?.firstCabinSelections?.selectedCabinPreference?.toLowerCase()
          : '',
      cabin_type_detail:
        state?.bookingStepsStore?.firstCabinSelections?.selectedCabinType?.toLowerCase()
          ? state?.bookingStepsStore?.firstCabinSelections?.selectedCabinType?.toLowerCase()
          : '',
      voyagers_club:
        (state?.bookingStepsStore?.firstCabinSelections?.voyagersMembersInfo &&
          state?.bookingStepsStore?.firstCabinSelections?.voyagersMembersInfo?.length > 0) ||
        (state?.bookingStepsStore?.secondCabinSelections?.voyagersMembersInfo &&
          state?.bookingStepsStore?.secondCabinSelections?.voyagersMembersInfo?.length > 0)
          ? 'yes'
          : 'no',
    }));

    window.didomiOnReady = window.didomiOnReady || [];
    console.log('Didomi Wrapper trackPurchaseDuringCheckout - About to push callback');
    window.didomiOnReady.push(() => {
      console.log('Didomi Wrapper trackPurchaseDuringCheckoutEpic called - Didomi is ready');
      console.log('didomiVendorsEnabled =', window.didomiState?.didomiVendorsEnabled);
      window.adobeDataLayer?.push({ ecommerce: null });
      console.log(
        'Custom event trackPurchaseDuringCheckout ecommerce null pushed into the data layer'
      );
    });

    const itemLecelCouponVal = sessionStorage?.getItem('itemLevelCoupon');
    let couponVal = '';
    if (state?.bookingStepsStore?.eCouponCode?.code) {
      couponVal = state?.bookingStepsStore?.eCouponCode?.code;
    } else if (itemLecelCouponVal) {
      couponVal = itemLecelCouponVal;
    }

    const pr =
      parseFloat(state?.bookingStepsStore?.firstCabinSelections?.cabinObj?.price) ||
      (state?.priceTypes?.priceTypes &&
        parseFloat(
          state?.priceTypes?.priceTypes[0]?.lowestPricedCategory?.totalAllInclusiveCabinPrice
            ?.afterDiscount
        ));

    let coupon = 0;
    if (state?.bookingStepsStore?.prices?.priceBreakdown[0]?.discounts) {
      for (
        let i = 0;
        i < state?.bookingStepsStore?.prices?.priceBreakdown[0]?.discounts.length;
        i++
      ) {
        if (state?.bookingStepsStore?.prices?.priceBreakdown[0]?.discounts[i].code == couponVal)
          coupon = Number(
            state?.bookingStepsStore?.prices?.priceBreakdown[0]?.discounts[i]?.amount
          );
      }
    }

    let finalPrice = (pr ?? 0) - coupon;

    finalPrice += getSelectedInsuranceTotal(state);

    const hasStrikethrough = sessionStorage?.getItem('hasStrikethrough');
    const bfStrikeThrough = (data as any)?.genericConfigs?.configs?.b2cCommerceConfig
      ?.strikethrough[0]?.enableStrikethroughBF
      ? 'yes'
      : 'no';
    let finalStrikethrough = 'no';
    if (hasStrikethrough == 'yes' && bfStrikeThrough == 'no') finalStrikethrough = hasStrikethrough;
    else if (calculateBFdiscount(data, state) > 0) finalStrikethrough = bfStrikeThrough;

    let pt;
    if (activePaymentMethod?.method == 'googleapplepay') pt = 'google pay';
    else if (activePaymentMethod?.method == 'paypal') pt = 'paypal';
    else
      pt =
        typeof activePaymentMethod?.method === 'string' &&
        (activePaymentMethod.type === 'totaltravelfare' || activePaymentMethod.type === 'deposit')
          ? getCardParameter((data as any)?.cardInfo?.brand) || ''
          : activePaymentMethod?.method || '';

    let hotelServiceChargesTotal = 0;
    if (state?.bookingStepsStore?.firstCabinSelections?.cabinObj?.hotelServiceChargesTotal) {
      hotelServiceChargesTotal = parseFloat(
        state?.bookingStepsStore?.firstCabinSelections?.cabinObj?.hotelServiceChargesTotal
      );
    }

    return {
      event: 'payment_failure',
      area_of_payment: 'booking funnel',
      algolia_object_id: algoliaObjectId(state),
      step_name: 'thankyou page',
      step_number: getPostPaymentStepNumber(state),
      gender:
        state?.bookingStepsStore?.firstCabinSelections?.passengersData[0]?.gender?.toLowerCase() ||
        '',
      age_group: getAgeGroup(
        state?.bookingStepsStore?.firstCabinSelections?.passengersData[0]?.dateOfBirth
      ),
      booking_id:
        state?.bookingStepsStore?.bookingResult?.bookingNumber !== -1
          ? state?.bookingStepsStore?.bookingResult?.bookingNumber
          : '',
      transaction_type: state?.bookingStepsStore?.bookingResult?.bookingType
        ? state.bookingStepsStore.bookingResult.bookingType.toLowerCase() === 'hold'
          ? 'option'
          : state.bookingStepsStore.bookingResult.bookingType.toLowerCase()
        : '',
      payment_type: pt,
      ecommerce: {
        transaction_id: activePaymentMethod
          ? state?.bookingStepsStore?.prices?.booking?.total?.transactionId
          : !state?.bookingStepsStore?.prices?.booking?.total ||
            !state?.bookingStepsStore?.prices?.booking?.deposit
          ? state?.bookingStepsStore?.merchantIdBooking
          : state?.bookingStepsStore?.prices?.booking?.deposit?.transactionId,
        value: getTotalPrice(state),
        currency: state?.genericConfigs?.configs?.b2cCommerceConfig?.currencyCode?.toLowerCase()
          ? state?.genericConfigs?.configs?.b2cCommerceConfig?.currencyCode?.toLowerCase()
          : '',
        coupon: state?.bookingStepsStore?.eCouponCode?.code
          ? state?.bookingStepsStore?.eCouponCode?.code
          : '',
        total_discount:
          Math.ceil(
            Number(totalDiscountCalculation(state, activePaymentMethod?.type || '')) * 100
          ) / 100 || 0,
        base_deposit_amount:
          activePaymentMethod?.type == 'deposit'
            ? Math.ceil(
                Number(state?.bookingStepsStore?.prices?.booking?.deposit?.afterDiscount) * 100
              ) / 100 || 0
            : 0,
        items: [
          {
            ...mapItemDataBooking(data, state),

            coupon:
              calculateCoupon(state) && finalStrikethrough == 'yes' ? calculateCoupon(state) : '',
            discount:
              Math.ceil(
                Number(totalDiscountCalculation(state, activePaymentMethod?.type || '')) * 100
              ) / 100 || 0,
            price: finalPrice + hotelServiceChargesTotal,
            cabin_preference_selected: getCabinPreferenceSelected(state, data as any),
            cabin_type_detail:
              state?.bookingStepsStore?.firstCabinSelections?.selectedCabinType?.toLowerCase()
                ? state?.bookingStepsStore?.firstCabinSelections?.selectedCabinType?.toLowerCase()
                : '',
            package_selected:
              state?.bookingStepsStore?.selectedPriceType?.lowestPricedCategory.priceDescription?.toLowerCase()
                ? state?.bookingStepsStore?.selectedPriceType?.lowestPricedCategory.priceDescription?.toLowerCase()
                : '',
            item_category4: retrieveExperience(
              state?.bookingStepsStore?.firstCabinSelections?.selectedCategory
            ),
            voyagers_club:
              (state?.bookingStepsStore?.firstCabinSelections?.voyagersMembersInfo &&
                state?.bookingStepsStore?.firstCabinSelections?.voyagersMembersInfo?.length > 0) ||
              (state?.bookingStepsStore?.secondCabinSelections?.voyagersMembersInfo &&
                state?.bookingStepsStore?.secondCabinSelections?.voyagersMembersInfo?.length > 0)
                ? 'yes'
                : 'no',
          },
          ...extraServicesItems,
        ],
      },
    };
  },
  'payment_failure'
);

// Analytics for search_excursion event
const trackSearchExcursionEventEpic = createAnalyticsEpic(
  trackSearchExcursionEvent.type, // The action type to listen for
  () => ({
    event: 'search_excursion',
    filters: {
      search_destination: '' /* TODO FIND THE SEARCH*/,
      search_departure_port: '' /* TODO FIND THE SEARCH*/,
      search_dep_port_category: '' /* TODO FIND THE SEARCH*/,
      search_date: '' /* TODO FIND THE SEARCH*/,
      search_guests: '' /* TODO FIND THE SEARCH*/,
      search_ships: '' /* TODO FIND THE SEARCH*/,
      search_promo: '' /* TODO FIND THE SEARCH*/,
      search_tag: '' /* TODO FIND THE SEARCH*/,
      search_price_min: '' /* TODO FIND THE SEARCH*/,
      search_price_max: '' /* TODO FIND THE SEARCH*/,
      search_exc_duration: '' /* TODO FIND THE SEARCH*/,
      search_exc_category: '' /* TODO FIND THE SEARCH*/,
    },
  }),
  'search_excursion'
);

//Analytics #2 search_cruise
const trackSearchCruiseEpic = createAnalyticsEpic(
  trackSearchCruise.type, // Action type to listen for
  (trigger, state) => {
    const urlValue = window.location.pathname.toLowerCase();
    const VPN = window.pageViewContent?.['virtual_page_name']?.toLowerCase() || '';

    const indexType = store?.getState().searchIndexTypeState?.searchIndexType;

    const isExcursion =
      urlValue.includes('excursion') ||
      VPN.includes('excursion') ||
      indexType.includes('excursion');
    const eventName = isExcursion ? 'search_excursion' : 'search_cruise';

    // Group destinations by name
    const selectedList = isExcursion
      ? state?.facetFilter?.selectedDestinationsExcursions
      : state?.facetFilter?.selectedDestinations;

    const groupedByName: Record<string, string[]> = selectedList.reduce((acc, { name, value }) => {
      if (!acc[name]) acc[name] = [];
      acc[name].push(value);
      return acc;
    }, {} as Record<string, string[]>);

    //Select ports for excursions or search
    const selectedPorts = isExcursion
      ? state?.facetFilter?.selectedPortsExcursions
      : state?.facetFilter?.selectedPorts;
    // Extract and combine only the values
    const combinedValues = Object.values(groupedByName)
      .map((values) => values.join('-'))
      .join(', ');

    // Define `filters` as `Record<string, any>` to allow dynamic keys
    const filters: Record<string, string> = {
      search_engagement_point: trigger as string,
      search_destination: combinedValues?.toLowerCase() || 'any',
      search_departure_port: arrayOfObjectsToString(selectedPorts),
      search_dep_port_category:
        arrayOfObjectsToString(selectedPorts, 'geographicalAreaName') || 'any',
      search_date:
        concatStartAndEndDate(
          state?.dateFilter?.selectedDates?.dates,
          state?.dateFilter?.selectedPeriods
        ) || 'any',
      search_guests: (
        state?.guestFilter?.firstCabin?.adults +
        (state?.guestFilter?.secondCabin?.isActive ? state?.guestFilter?.secondCabin?.adults : 0)
      ).toString(),
      search_cabin_num: state?.guestFilter?.secondCabin?.isActive ? '2' : '1',
      search_cabin_accessible:
        state?.guestFilter?.firstCabin?.isAccessible ||
        state?.guestFilter?.secondCabin?.isAccessible
          ? 'yes'
          : 'no',
      search_cabin_connected: state?.guestFilter?.secondCabin?.isConnectedCabin ? 'yes' : 'no',
      search_cruise_length:
        arrayOfObjectsToString(state?.genericFilter?.numberOfNights, 'name') || 'any',
      search_ships: arrayOfObjectsToString(state?.facetFilter?.selectedShips) || 'any',
      search_promo: arrayOfObjectsToString(state?.genericFilter?.promotion, 'name') || 'any',
      search_tag: arrayOfObjectsToString(state?.genericFilter?.tag, 'name') || 'any',
      search_fly_cruise:
        state?.genericFilter?.flyAndCruise?.some(
          (item: { value: string }) => item.value === 'hasAir'
        ) || getQueryParam('flyAndCruise') == 'hasAir'
          ? 'yes'
          : 'no',
      search_price_min:
        state?.facetFilter?.priceRange
          .find((item) => item.name === 'minPrice')
          ?.value?.toString() || 'any',
      search_price_max:
        state?.facetFilter?.priceRange
          .find((item) => item.name === 'maxPrice')
          ?.value?.toString() || 'any',
      search_cabin_type:
        arrayOfObjectsToString(state?.facetFilter?.selectedCabins, 'name') || 'any',
    };

    // Add extra filters only for `search_excursion`
    if (isExcursion) {
      filters['search_exc_duration'] = 'any';

      filters['search_exc_category'] =
        state?.facetFilter?.selectedCategories
          ?.map((category: { name: string }) => category.name.toLowerCase())
          .join(', ') || 'any';

      // Remove fields that do not apply to excursions
      const fieldsToRemove = [
        'search_cabin_accessible',
        'search_cabin_connected',
        'search_cabin_num',
        'search_cabin_type',
        'search_cruise_length',
        'search_engagement_point',
        'search_fly_cruise',
        'search_exc_duration',
        'search_date',
        'search_price_max',
        'search_price_min',
        'search_promo',
        'search_ships',
        'search_tag',
        'search_exc_term',
      ];

      fieldsToRemove.forEach((field) => delete filters[field]);
      return { event: eventName, filters };
    }

    return { event: eventName, filters };
  },
  'search_event'
);

const trackViewItemListEpic = createAnalyticsEpic(
  trackViewItemList.type,
  (listData, state) => {
    window.didomiOnReady = window.didomiOnReady || [];
    console.log('Didomi Wrapper trackViewItemList - About to push callback');
    window.didomiOnReady.push(() => {
      console.log('Didomi Wrapper trackViewItemListEpic called - Didomi is ready');
      console.log('didomiVendorsEnabled =', window.didomiState?.didomiVendorsEnabled);
      window.adobeDataLayer?.push({ ecommerce: null });
      console.log('Custom event view_item_list pushed into the data layer');
    });
    mapItemCounter = 0;

    const urlValue = window.location.pathname.toLowerCase();
    const VPN = window.pageViewContent?.['virtual_page_name']?.toLowerCase() || '';
    const indexType = store.getState().searchIndexTypeState.searchIndexType;
    const isExcursion =
      urlValue.includes('excursion') ||
      VPN.includes('excursion') ||
      indexType.includes('excursion');

    const startAtPageSessionStorage = mscGet<string>('session', 'started_at');
    const virtualPageNameSessionStorage = mscGet<string>('session', 'virtualPageName');
    let itemListName = '';
    if (isExcursion) {
      itemListName =
        'search exc - ' + (startAtPageSessionStorage != null ? startAtPageSessionStorage : '');
    } else if (VPN.includes('search')) {
      itemListName =
        'search - ' + (startAtPageSessionStorage != null ? startAtPageSessionStorage : '');
    } else {
      itemListName = 'listing - ' + virtualPageNameSessionStorage;
    }

    sessionStorage?.setItem('itemListName', itemListName);

    if (isExcursion) {
      return {
        ecommerce: {
          items: (listData as any[]).map((data: any, idx: number) => ({
            ...mapItemData(data, state),
            item_list_name: itemListName,
            discount: 0, //To be added when information available
            item_brand: '', //To be added when information available
            item_category3: '', //To be added when information available
            item_variant: 'prepaid',
            cabin_accessible: '', ///default '' for now
            cabin_connected: '', ///default '' for now
            cabin_num: '', ///default '' for now
            cruise_type: '', ///default '' for now
            item_category: 'prepaid',
            item_category2: (listData as any[])[idx]?.type, ////bres timh
            adults_number: '',
            number_passengers: '', ///default '' for now
            cruise_promo_badge: [
              (listData as any[])[idx]?.ribbons?.['limitedSeats']?.display
                ? (listData as any[])[idx]?.ribbons?.['limitedSeats']?.text
                : null,
              (listData as any[])[idx]?.ribbons?.['recommended']?.display
                ? (listData as any[])[idx]?.ribbons?.['recommended']?.text
                : null,
              (listData as any[])[idx]?.details?.['best-seller']
                ? (listData as any[])[idx]?.labels?.['best-seller']
                : null,
              (listData as any[])[idx]?.details?.['lunch']
                ? (listData as any[])[idx]?.labels?.['lunch']
                : null,
            ]
              .filter(Boolean)
              .join(':')
              .toLowerCase(),
            index:
              idx + 1
                ? idx +
                  1 +
                  state?.search?.excursionsData?.hitsPerPage * state?.pagination?.activePageIndex
                : '',
            item_name: (listData as any[])[idx]?.title ? (listData as any[])[idx]?.title : '',
            item_id: (listData as any[])[idx]?.objectID
              ? (listData as any[])[idx]?.objectID.toLowerCase()
              : '',
          })),
        },
      };
    } else {
      return {
        ecommerce: {
          items: (listData as any[]).map((data: any, idx: number) => ({
            ...mapItemData(data, state),
            index:
              idx + 1
                ? idx +
                  1 +
                  state?.search?.itineraryData?.hitsPerPage * state?.pagination?.activePageIndex
                : '',
          })),
        },
      };
    }
  },
  'view_item_list'
);

const trackSelectItemEpic = createAnalyticsEpic(
  trackSelectItem.type,
  (itineraryData, state) => {
    const urlValue = window.location.pathname.toLowerCase();
    const VPN = window.pageViewContent?.['virtual_page_name']?.toLowerCase() || '';
    const indexType = store.getState().searchIndexTypeState.searchIndexType;
    const isExcursion =
      urlValue.includes('excursion') ||
      VPN.includes('excursion') ||
      indexType.includes('excursion');

    const eventName = isExcursion ? 'view_item' : 'select_item';

    window.didomiOnReady = window.didomiOnReady || [];
    console.log('Didomi Wrapper trackSelectItem - About to push callback');
    window.didomiOnReady.push(() => {
      console.log('Didomi Wrapper trackSelectItemEpic called - Didomi is ready');
      console.log('didomiVendorsEnabled =', window.didomiState?.didomiVendorsEnabled);
      window.adobeDataLayer?.push({ ecommerce: null });
      console.log('Custom event select_item pushed into the data layer');
    });
    mapItemCounter = 1000;

    if (isExcursion) {
      let finalData;
      let index: number | undefined = -1; // Example value
      const hits: Array<Hit> = store.getState()?.search?.excursionsData?.hits as Array<Hit>;

      if (hits.length > 1) {
        const id = store.getState()?.excursionsCard?.selectedEscursionsCard?.objectID;

        index = hits.findIndex((hit) => hit.objectID === id);

        if (index !== -1) {
          finalData = hits[index]; // Assign the found hit
        }
      }

      const startAtPageSessionStorage = mscGet<string>('session', 'started_at');
      const virtualPageNameSessionStorage = mscGet<string>('session', 'virtualPageName');
      let itemListName = '';
      if (isExcursion) {
        itemListName =
          'search exc - ' + (startAtPageSessionStorage != null ? startAtPageSessionStorage : '');
      } else if (VPN.includes('search')) {
        itemListName =
          'search - ' + (startAtPageSessionStorage != null ? startAtPageSessionStorage : '');
      } else {
        itemListName = 'listing - ' + virtualPageNameSessionStorage;
      }
      sessionStorage?.setItem('itemListName', itemListName);

      return {
        event: eventName,
        ecommerce: {
          items: [
            {
              ...mapItemData(finalData, state),
              index:
                index + 1
                  ? index +
                    1 +
                    state?.search?.excursionsData?.hitsPerPage * state?.pagination?.activePageIndex
                  : '',
              item_name: store.getState()?.excursionsCard?.selectedEscursionsCard?.title
                ? store.getState()?.excursionsCard?.selectedEscursionsCard?.title
                : '',
              item_id: store.getState()?.excursionsCard?.selectedEscursionsCard?.objectID
                ? store.getState()?.excursionsCard?.selectedEscursionsCard?.objectID.toLowerCase()
                : '',
              discount: '', ///default '' for now
              item_brand: '', //To be added when information available
              item_category3: '', //To be added when information available
              item_variant: 'prepaid',
              cabin_accessible: '', ///default '' for now
              cabin_connected: '', ///default '' for now
              cabin_num: '', ///default '' for now
              cruise_type: '', ///default '' for now
              item_category: 'prepaid',
              item_category2: store.getState()?.excursionsCard?.selectedEscursionsCard?.type,
              adults_number: '',
              number_passengers: '', ///default '' for now
              cruise_promo_badge: [
                store.getState()?.excursionsCard?.selectedEscursionsCard?.ribbons?.['limitedSeats']
                  ?.display
                  ? store.getState()?.excursionsCard?.selectedEscursionsCard?.ribbons?.[
                      'limitedSeats'
                    ]?.text
                  : null,
                store.getState()?.excursionsCard?.selectedEscursionsCard?.ribbons?.['recommended']
                  ?.display
                  ? store.getState()?.excursionsCard?.selectedEscursionsCard?.ribbons?.[
                      'recommended'
                    ]?.text
                  : null,
                store.getState()?.excursionsCard?.selectedEscursionsCard?.details?.['best-seller']
                  ? store.getState()?.excursionsCard?.selectedEscursionsCard?.labels?.[
                      'best-seller'
                    ]
                  : null,
                store.getState()?.excursionsCard?.selectedEscursionsCard?.details?.['lunch']
                  ? store.getState()?.excursionsCard?.selectedEscursionsCard?.labels?.['lunch']
                  : null,
              ]
                .filter(Boolean)
                .join(':')
                .toLowerCase(),
              item_list_name: itemListName,
            },
          ],
        },
      };
    }

    if ((itineraryData as any)?.hotelServiceCharges?.[0]?.totalPrice) {
      mscSet(
        'session',
        'HotelFeeTotal',
        (itineraryData as any)?.hotelServiceCharges[0]?.totalPrice
      );
    }

    return {
      event: eventName,
      ecommerce: {
        items: [{ ...mapItemData(itineraryData, state) }],
      },
    };
  },
  'select_item'
);

//Analytics #5 view_item
const trackViewItemEpic = createAnalyticsEpic(
  trackViewItem.type,
  (data, state) => {
    mapItemCounter = 1000;
    let finalData;
    let index: number | undefined; // Example value
    const itemOrder = mscGet<Record<string, number>>('session', 'item_order');

    index = Number(mscGet<number | string>('session', 'finalIndex') ?? 0);
    if (index == 1) {
      index = Number(mscGet<number | string>('session', 'finalIndex') ?? 1);
    } else {
      mscSet('session', 'finalIndex', index?.toString() || '0');
      if (itemOrder) {
        // Look for the matching number using data?.cruiseId as the key
        const matchingNumber = itemOrder[data as string];
        // If a match is found, store the corresponding number in index
        if (matchingNumber !== undefined) {
          index = matchingNumber;
        } else {
          console.warn(`No matching cruiseId found for ${(data as any)?.cruiseId}`);
        }
      }
    }

    if (typeof data === 'string') {
      //check if i got just the id or the full cruise
      let hits: Array<Hit> = store.getState()?.search?.itineraryData?.hits as Array<Hit>;
      if (hits.length > 1) {
        hits = hits.filter((item) => item.cruiseID === data);
      } else if (!hits || hits.length === 0) {
        console.error('No hits found in itinerary data for "view_item" event');
        return {};
      }
      finalData = hits[0];
    } else {
      finalData = data;
    }
    window.didomiOnReady = window.didomiOnReady || [];
    console.log('Didomi Wrapper trackViewItem - About to push callback');
    window.didomiOnReady.push(() => {
      console.log('Didomi Wrapper trackViewItemEpic called - Didomi is ready');
      console.log('didomiVendorsEnabled =', window.didomiState?.didomiVendorsEnabled);
      window.adobeDataLayer?.push({ ecommerce: null });
      console.log('Custom event view_item pushed into the data layer');
    });

    return {
      ecommerce: {
        currency: state?.genericConfigs?.configs?.b2cCommerceConfig?.currencyCode?.toLowerCase()
          ? state?.genericConfigs?.configs?.b2cCommerceConfig?.currencyCode?.toLowerCase()
          : '',
        value: parseFloat((finalData as any)?.pricesAfterDiscount?.cabinPrice),
        items: [
          {
            ...mapItemData(finalData, state),
            index: index,
          },
        ],
      },
    };
  },
  'view_item'
);

//Analytics #6 add_to_wishlist
const trackAddToWishlistEpic = createAnalyticsEpic(
  trackAddToWishlist.type,
  (itineraryData, state) => {
    let indexIn = 0; // Default to 0 if no valid index is found
    // Push the tracking data with the determined index
    window.didomiOnReady = window.didomiOnReady || [];
    console.log('Didomi Wrapper trackAddToWishlist - About to push callback');
    window.didomiOnReady.push(() => {
      console.log('Didomi Wrapper trackAddToWishlistEpic called - Didomi is ready');
      console.log('didomiVendorsEnabled =', window.didomiState?.didomiVendorsEnabled);
      window.adobeDataLayer?.push({ ecommerce: null });
      console.log('Custom event add_to_wishlist pushed into the data layer');
    });
    mapItemCounter = 1000;

    let finalPrice = 0;
    const strikeThroughPrice = 0;
    let discount = 0;

    const virtualPageName = sessionStorage?.getItem('virtualPageName');
    const startedAtPageName = sessionStorage?.getItem('started_at')
      ? sessionStorage?.getItem('virtualPageName')
      : '';
    let itemListName = '';
    if (virtualPageName?.includes('search')) {
      itemListName = 'search - ' + (startedAtPageName != null ? startedAtPageName : '');
      sessionStorage?.setItem('itemListName', itemListName);
    } else {
      itemListName = 'listing - ' + virtualPageName;
      sessionStorage?.setItem('itemListName', itemListName);
    }
    // Retrieve the last item added to the wishlist from localStorage
    const wishlistItems = JSON.parse(localStorage?.getItem('wishlist') ?? '[]');
    const lastItem = wishlistItems[wishlistItems?.length - 1];
    // If we're on the "home" page, proceed with the special logic

    if (virtualPageName === 'home' || virtualPageName?.includes('ships')) {
      // Check if the current itinerary is the last item added to the wishlist
      if ((itineraryData as any)?.itinCd === lastItem) {
        // Find the swiper that contains the itineraryCode
        const matchingSlide = Array.from(
          document?.querySelectorAll('.swiper-wrapper .swiper-slide')
        ).find((slide) => {
          const link = slide?.querySelector('a');
          return link?.href?.includes((itineraryData as any)?.itinCd); // Match the itineraryCode
        });

        if (matchingSlide) {
          const ariaLabel = matchingSlide?.getAttribute('aria-label');
          // Extract the first number from the aria-label (e.g., '7 / 8')
          indexIn = ariaLabel ? parseInt(ariaLabel?.split(' / ')[0]) : -1;
        }
      }
      finalPrice = (itineraryData as any)?.prices?.cabinPrice;

      if (strikeThroughPrice) {
        discount = strikeThroughPrice - finalPrice;
      }
    }
    const paxType = getPaxType(state?.search?.params?.filters, itineraryData);
    return {
      ecommerce: {
        currency:
          state?.genericConfigs?.configs?.b2cCommerceConfig?.currencyCode?.toLowerCase() || '',
        value:
          virtualPageName === 'home' || virtualPageName?.includes('ships')
            ? strikeThroughPrice == 0
              ? finalPrice
              : strikeThroughPrice
            : !isDealsPage()
            ? (itineraryData as any)?.pricesAfterDiscount?.cabinPrice
              ? parseFloat((itineraryData as any)?.pricesAfterDiscount?.cabinPrice)
              : ''
            : calculateDiscount(itineraryData, state) > 0 && paxType
            ? calculatePriceDeals(itineraryData, state)
            : (itineraryData as any)?.pricesAfterDiscount?.cabinPrice
            ? parseFloat((itineraryData as any)?.pricesAfterDiscount?.cabinPrice)
            : '',
        items: [
          {
            ...mapItemData(itineraryData, state),
            index:
              indexIn != 0
                ? indexIn
                : Number(mscGet<number | string>('session', 'finalIndex') ?? 0),
            item_list_name: itemListName || '',
            ...(virtualPageName === 'home' || virtualPageName?.includes('ships')
              ? {
                  price: strikeThroughPrice == 0 ? finalPrice : strikeThroughPrice,
                  discount: strikeThroughPrice !== 0 ? discount : 0,
                }
              : {}),
          },
        ],
      },
    };
  },
  'add_to_wishlist'
);

// Analytics #7 cruise_share
const trackCruiseShareEpic = createAnalyticsEpic(
  trackCruiseShare.type,
  (itineraryData, state) => {
    window.didomiOnReady = window.didomiOnReady || [];
    console.log('Didomi Wrapper trackCruiseShare - About to push callback');
    window.didomiOnReady.push(() => {
      console.log('Didomi Wrapper trackCruiseShareEpic called - Didomi is ready');
      console.log('didomiVendorsEnabled =', window.didomiState?.didomiVendorsEnabled);
      window.adobeDataLayer?.push({ ecommerce: null });
      console.log('Custom event cruise_share pushed into the data layer');
    });
    mapItemCounter = 1000;
    const paxType = getPaxType(state?.search?.params?.filters, itineraryData);
    return {
      ecommerce: {
        currency: state?.genericConfigs?.configs?.b2cCommerceConfig?.currencyCode?.toLowerCase()
          ? state?.genericConfigs?.configs?.b2cCommerceConfig?.currencyCode?.toLowerCase()
          : '',
        value: !isDealsPage()
          ? (itineraryData as any)?.pricesAfterDiscount?.cabinPrice
            ? parseFloat((itineraryData as any)?.pricesAfterDiscount?.cabinPrice)
            : ''
          : calculateDiscount(itineraryData, state) > 0 && paxType
          ? calculatePriceDeals(itineraryData, state)
          : (itineraryData as any)?.pricesAfterDiscount?.cabinPrice
          ? parseFloat((itineraryData as any)?.pricesAfterDiscount?.cabinPrice)
          : '',
        items: [
          {
            ...mapItemData(itineraryData, state),
            index: Number(mscGet<number | string>('session', 'finalIndex') ?? 0),
          },
        ],
      },
    };
  },
  'cruise_share'
);

// Analytics #8 add_to_cart
const trackAddToCartEpic = createAnalyticsEpic(
  trackAddToCart.type,
  (payload = {}, state: RootState) => {
    const { date, data } = payload as any;
    window.didomiOnReady = window.didomiOnReady || [];
    console.log('Didomi Wrapper trackAddToCart - About to push callback');
    window.didomiOnReady.push(() => {
      console.log('Didomi Wrapper trackAddToCartEpic called - Didomi is ready');
      console.log('didomiVendorsEnabled =', window.didomiState?.didomiVendorsEnabled);
      window.adobeDataLayer?.push({ ecommerce: null });
      console.log('Custom event add_to_cart pushed into the data layer');
    });
    const index = sessionStorage?.getItem('finalIndex');
    mapItemCounter = 1000;

    const itemData = mapItemData(data, state);
    const departureDate =
      getQueryParam('flyAndCruise') !== 'hasAir'
        ? `${date.startDate.getFullYear()}-${String(date.startDate.getMonth() + 1).padStart(
            2,
            '0'
          )}-${String(date.startDate.getDate()).padStart(2, '0')}`
        : date?.startDate || '';

    const arrivalDate =
      getQueryParam('flyAndCruise') !== 'hasAir'
        ? `${date.endDate.getFullYear()}-${String(date.endDate.getMonth() + 1).padStart(
            2,
            '0'
          )}-${String(date.endDate.getDate()).padStart(2, '0')}`
        : date?.endDate || '';

    mscSet('session', 'finalIndex', index?.toString() || '0');
    const flightPriceFromSession = mscGet<number | string>('session', 'flightPrice');
    const flightPrice: number = flightPriceFromSession ? Number(flightPriceFromSession) : 0;

    sessionStorage.removeItem('flightPrice');
    let finalPrice = data?.pricesAfterDiscount?.cabinPrice
      ? data?.pricesAfterDiscount?.cabinPrice
      : '';
    if (flightPrice) finalPrice = finalPrice + flightPrice;

    const paxType = getPaxType(state?.search?.params?.filters, data);
    return {
      ecommerce: {
        currency:
          state?.genericConfigs?.configs?.b2cCommerceConfig?.currencyCode?.toLowerCase() || '',
        value: !isDealsPage()
          ? finalPrice
            ? parseFloat(finalPrice)
            : ''
          : calculateDiscount(data, state) > 0 && paxType
          ? calculatePriceDeals(data, state)
          : finalPrice
          ? parseFloat(finalPrice)
          : '',
        items: [
          {
            ...itemData,
            price: !isDealsPage()
              ? finalPrice
                ? parseFloat(finalPrice)
                : ''
              : calculateDiscount(data, state) > 0 && paxType
              ? calculatePriceDeals(data, state)
              : finalPrice
              ? parseFloat(finalPrice)
              : '',
            index: parseInt(index || '0'),
            cruise_starting_date: departureDate ? departureDate : '',
            cruise_ending_date: arrivalDate ? arrivalDate : '',
            fly_cruise_selected: state?.dateslayer?.isFlightToggleEnabled ? 'yes' : 'no',
            fly_departure:
              state?.flightData?.selectedAirfare?.outbound?.segments?.[0]?.origin?.airport?.code ||
              '',
          },
        ],
      },
    };
  },
  'add_to_cart'
);

const trackBeginCheckoutEpic = createAnalyticsEpic(
  trackBeginCheckout.type,
  (data, state) => {
    window.didomiOnReady = window.didomiOnReady || [];
    console.log('Didomi Wrapper trackBeginCheckout - About to push callback');
    window.didomiOnReady.push(() => {
      console.log('Didomi Wrapper trackBeginCheckoutEpic called - Didomi is ready');
      console.log('didomiVendorsEnabled =', window.didomiState?.didomiVendorsEnabled);
      window.adobeDataLayer?.push({ ecommerce: null });
      console.log('Custom event begin_checkout pushed into the data layer');
    });

    let parsedCData = null;
    const cResponse = mscGet<any>('session', 'cruiseResponse');
    if (cResponse) parsedCData = cResponse;
    sessionStorage.removeItem('cruiseResponse');

    let hotelServiceChargesTotal = 0;
    if (state?.bookingStepsStore?.firstCabinSelections?.cabinObj?.hotelServiceChargesTotal) {
      hotelServiceChargesTotal = parseFloat(
        state?.bookingStepsStore?.firstCabinSelections?.cabinObj?.hotelServiceChargesTotal
      );
    }

    return {
      step_name: 'package selection',
      step_number: '1',
      area_of_payment: 'booking funnel',
      ecommerce: {
        currency: state?.genericConfigs?.configs?.b2cCommerceConfig?.currencyCode?.toLowerCase()
          ? state?.genericConfigs?.configs?.b2cCommerceConfig?.currencyCode?.toLowerCase()
          : '',
        value: getTotalPrice(state),
        items: [
          {
            ...mapItemDataBooking(data, state),
            arrival_port: parsedCData ? parsedCData.departurePort.toLowerCase() : '',
            cruise_ending_date: parsedCData ? parsedCData.arrivalDate : '',
            cruise_length: getDaysInclusive(
              parsedCData?.departureDate || '',
              parsedCData?.arrivalDate || ''
            ).toString(),
            cruise_starting_date: parsedCData ? parsedCData.departureDate : '',
            cruise_title: parsedCData ? parsedCData.title.toLowerCase() : '',
            departing_port: parsedCData ? parsedCData.arrivalPort.toLowerCase() : '',
            item_brand: parsedCData ? parsedCData.brand.toLowerCase() : '',
            item_id: parsedCData ? parsedCData.cruiseID : '',
            item_name: sessionStorage?.getItem('itemName')
              ? sessionStorage?.getItem('itemName')
              : store.getState().cruiseData?.name?.toLowerCase() ?? '',
            package_available: (() => {
              const priceTypesArray = state?.priceTypes?.priceTypes as any[]; // Ensure it's treated as an array

              if (!priceTypesArray || priceTypesArray.length === 0) return '';

              // If there is only one item → "pre selected"
              if (priceTypesArray.length === 1) {
                return 'pre selected';
              }

              // Otherwise collect all nested priceDescription values
              const descriptions = priceTypesArray
                .map((item) => item?.lowestPricedCategory?.priceDescription)
                .filter(Boolean); // remove undefined/null

              // Join with semicolon (or whatever your concatStringWithSemiColon was doing)
              return descriptions.join(':').toLowerCase();
            })(),
          },
        ],
      },
    };
  },
  'begin_checkout'
);

const trackCheckoutProgressGuestAndCabinEpic = createAnalyticsEpic(
  trackCheckoutProgressGuestAndCabin.type,
  (data, state) => {
    window.didomiOnReady = window.didomiOnReady || [];
    console.log('Didomi Wrapper trackCheckoutProgressGuestAndCabin - About to push callback');
    window.didomiOnReady.push(() => {
      console.log('Didomi Wrapper trackCheckoutProgressGuestAndCabinEpic called - Didomi is ready');
      console.log('didomiVendorsEnabled =', window.didomiState?.didomiVendorsEnabled);
      window.adobeDataLayer?.push({ ecommerce: null });
      console.log('Custom event checkout_progress pushed into the data layer');
    });
    let hotelServiceChargesTotal = 0;
    if (state?.bookingStepsStore?.firstCabinSelections?.cabinObj?.hotelServiceChargesTotal) {
      hotelServiceChargesTotal = parseFloat(
        state?.bookingStepsStore?.firstCabinSelections?.cabinObj?.hotelServiceChargesTotal
      );
    }
    return {
      step_name: 'guests and cabins confirmation',
      step_number: '2',
      area_of_payment: 'booking funnel',
      ecommerce: {
        currency: state?.genericConfigs?.configs?.b2cCommerceConfig?.currencyCode?.toLowerCase()
          ? state?.genericConfigs?.configs?.b2cCommerceConfig?.currencyCode?.toLowerCase()
          : '',
        value:
          parseFloat(
            state?.bookingStepsStore?.firstCabinSelections?.cabinObj?.price ||
              state?.priceTypes?.priceTypes?.[0]?.lowestPricedCategory?.totalAllInclusiveCabinPrice
                ?.afterDiscount ||
              '0' // fallback to "0" if neither is found
          ) + hotelServiceChargesTotal,
        items: [
          {
            ...mapItemDataBooking(data, state),
            item_category4: '', // this field will be sent empty until 'cabin location' step
            package_selected:
              state?.bookingStepsStore?.selectedPriceType?.lowestPricedCategory.priceDescription?.toLowerCase()
                ? state?.bookingStepsStore?.selectedPriceType?.lowestPricedCategory.priceDescription?.toLowerCase()
                : '',
            cabin_preference_available: '', // this field will be sent empty until 'cabin preferences' step
            cabin_preference_selected: '', // this field will be sent empty until 'cabin type details' step
            cabin_type_detail: '', // this field will be sent empty until 'cabin location' step
          },
        ],
      },
    };
  },
  'checkout_progress'
);

const trackCheckoutCabinTypeSelectionEpic = createAnalyticsEpic(
  trackCheckoutCabinTypeSelection.type,
  (data, state) => {
    window.didomiOnReady = window.didomiOnReady || [];
    console.log('Didomi Wrapper trackCheckoutCabinTypeSelection - About to push callback');
    window.didomiOnReady.push(() => {
      console.log('Didomi Wrapper trackCheckoutCabinTypeSelectionEpic called - Didomi is ready');
      console.log('didomiVendorsEnabled =', window.didomiState?.didomiVendorsEnabled);
      window.adobeDataLayer?.push({ ecommerce: null });
      console.log('Custom event checkout_progress pushed into the data layer');
    });
    return {
      step_name: 'cabin type selection',
      step_number: '3',
      area_of_payment: 'booking funnel',
      ecommerce: {
        currency: state?.genericConfigs?.configs?.b2cCommerceConfig?.currencyCode?.toLowerCase()
          ? state?.genericConfigs?.configs?.b2cCommerceConfig?.currencyCode?.toLowerCase()
          : '',
        value: getTotalPrice(state),
        items: [
          {
            ...mapItemDataBooking(data, state),
            item_category4: '', // this field will be sent empty until 'cabin location' step
            cabin_type_available: cabinsTypeAvailable(state),
            package_selected:
              state?.bookingStepsStore?.selectedPriceType?.lowestPricedCategory.priceDescription?.toLowerCase()
                ? state?.bookingStepsStore?.selectedPriceType?.lowestPricedCategory.priceDescription?.toLowerCase()
                : '',
            cabin_preference_available: '', // this field will be sent empty until 'cabin preferences' step
            cabin_preference_selected: '', // this field will be sent empty until 'cabin type details' step
            cabin_type_detail: '', // this field will be sent empty until 'cabin location' step
            voyagers_club:
              (state?.bookingStepsStore?.firstCabinSelections?.voyagersMembersInfo &&
                state?.bookingStepsStore?.firstCabinSelections?.voyagersMembersInfo?.length > 0) ||
              (state?.bookingStepsStore?.secondCabinSelections?.voyagersMembersInfo &&
                state?.bookingStepsStore?.secondCabinSelections?.voyagersMembersInfo?.length > 0)
                ? 'yes'
                : 'no',
          },
        ],
      },
    };
  },
  'checkout_progress'
);

const trackCheckoutCabinPreferencesEpic = createAnalyticsEpic(
  trackCheckoutCabinPreferences.type,
  (data, state) => {
    window.didomiOnReady = window.didomiOnReady || [];
    console.log('Didomi Wrapper trackCheckoutCabinPreferences - About to push callback');
    window.didomiOnReady.push(() => {
      console.log('Didomi Wrapper trackCheckoutCabinPreferencesEpic called - Didomi is ready');
      console.log('didomiVendorsEnabled =', window.didomiState?.didomiVendorsEnabled);
      window.adobeDataLayer?.push({ ecommerce: null });
      console.log('Custom event checkout_progress pushed into the data layer');
    });
    return {
      step_name: 'cabin preferences',
      step_number: '4',
      area_of_payment: 'booking funnel',
      ecommerce: {
        currency: state?.genericConfigs?.configs?.b2cCommerceConfig?.currencyCode?.toLowerCase()
          ? state?.genericConfigs?.configs?.b2cCommerceConfig?.currencyCode?.toLowerCase()
          : '',
        value: getTotalPrice(state),
        items: [
          {
            ...mapItemDataBooking(data, state),
            item_category4: '', // this field will be sent empty until 'cabin location' step
            package_selected:
              state?.bookingStepsStore?.selectedPriceType?.lowestPricedCategory.priceDescription?.toLowerCase()
                ? state?.bookingStepsStore?.selectedPriceType?.lowestPricedCategory.priceDescription?.toLowerCase()
                : '',
            cabin_preference_available: concatStringWithSemiColon(
              state?.cabinPreferencesStore?.cabinPreferences,
              'cabinPreference'
            ),
            cabin_preference_selected: '', // This field will be sent empty until cabin type selection.
            cabin_type_detail: '', // this field will be sent empty until 'cabin location' step
            voyagers_club:
              (state?.bookingStepsStore?.firstCabinSelections?.voyagersMembersInfo &&
                state?.bookingStepsStore?.firstCabinSelections?.voyagersMembersInfo?.length > 0) ||
              (state?.bookingStepsStore?.secondCabinSelections?.voyagersMembersInfo &&
                state?.bookingStepsStore?.secondCabinSelections?.voyagersMembersInfo?.length > 0)
                ? 'yes'
                : 'no',
          },
        ],
      },
    };
  },
  'checkout_progress'
);

const trackCheckoutCabinTypeDetailsEpic = createAnalyticsEpic(
  trackCheckoutCabinTypeDetails.type,
  (data, state) => {
    window.didomiOnReady = window.didomiOnReady || [];
    console.log('Didomi Wrapper trackCheckoutCabinTypeDetails - About to push callback');
    window.didomiOnReady.push(() => {
      console.log('Didomi Wrapper trackCheckoutCabinTypeDetailsEpic called - Didomi is ready');
      console.log('didomiVendorsEnabled =', window.didomiState?.didomiVendorsEnabled);
      window.adobeDataLayer?.push({ ecommerce: null });
      console.log('Custom event checkout_progress pushed into the data layer');
    });
    return {
      step_name: 'cabin type details',
      step_number: '5',
      area_of_payment: 'booking funnel',
      ecommerce: {
        currency: state?.genericConfigs?.configs?.b2cCommerceConfig?.currencyCode?.toLowerCase()
          ? state?.genericConfigs?.configs?.b2cCommerceConfig?.currencyCode?.toLowerCase()
          : '',
        value: getTotalPrice(state),
        items: [
          {
            ...mapItemDataBooking(data, state),
            item_category4: '', // this field will be sent empty until 'cabin location' step
            package_selected:
              state?.bookingStepsStore?.selectedPriceType?.lowestPricedCategory.priceDescription?.toLowerCase()
                ? state?.bookingStepsStore?.selectedPriceType?.lowestPricedCategory.priceDescription?.toLowerCase()
                : '',
            cabin_preference_selected:
              state?.bookingStepsStore?.firstCabinSelections?.selectedCabinPreference?.toLowerCase()
                ? state?.bookingStepsStore?.firstCabinSelections?.selectedCabinPreference?.toLowerCase()
                : '',
            cabin_type_detail: '', // this field will be sent empty until 'cabin location' step
            voyagers_club:
              (state?.bookingStepsStore?.firstCabinSelections?.voyagersMembersInfo &&
                state?.bookingStepsStore?.firstCabinSelections?.voyagersMembersInfo?.length > 0) ||
              (state?.bookingStepsStore?.secondCabinSelections?.voyagersMembersInfo &&
                state?.bookingStepsStore?.secondCabinSelections?.voyagersMembersInfo?.length > 0)
                ? 'yes'
                : 'no',
          },
        ],
      },
    };
  },
  'checkout_progress'
);

const trackCheckoutCabinLocationEpic = createAnalyticsEpic(
  trackCheckoutCabinLocation.type,
  (data, state) => {
    window.didomiOnReady = window.didomiOnReady || [];
    console.log('Didomi Wrapper trackCheckoutCabinLocation - About to push callback');
    window.didomiOnReady.push(() => {
      console.log('Didomi Wrapper trackCheckoutCabinLocationEpic called - Didomi is ready');
      console.log('didomiVendorsEnabled =', window.didomiState?.didomiVendorsEnabled);
      window.adobeDataLayer?.push({ ecommerce: null });
      console.log('Custom event checkout_progress pushed into the data layer');
    });
    return {
      step_name: 'cabin location',
      step_number: '6',
      area_of_payment: 'booking funnel',
      ecommerce: {
        currency: state?.genericConfigs?.configs?.b2cCommerceConfig?.currencyCode?.toLowerCase()
          ? state?.genericConfigs?.configs?.b2cCommerceConfig?.currencyCode?.toLowerCase()
          : '',
        value: getTotalPrice(state),
        items: [
          {
            ...mapItemDataBooking(data, state),
            item_category4: retrieveExperience(
              state?.bookingStepsStore?.firstCabinSelections?.selectedCategory
            ),
            package_selected:
              state?.bookingStepsStore?.selectedPriceType?.lowestPricedCategory.priceDescription?.toLowerCase()
                ? state?.bookingStepsStore?.selectedPriceType?.lowestPricedCategory.priceDescription?.toLowerCase()
                : '',
            cabin_preference_selected:
              state?.bookingStepsStore?.firstCabinSelections?.selectedCabinPreference?.toLowerCase()
                ? state?.bookingStepsStore?.firstCabinSelections?.selectedCabinPreference?.toLowerCase()
                : '',
            cabin_type_detail:
              state?.bookingStepsStore?.firstCabinSelections?.selectedCabinType?.toLowerCase()
                ? state?.bookingStepsStore?.firstCabinSelections?.selectedCabinType?.toLowerCase()
                : '',
            voyagers_club:
              (state?.bookingStepsStore?.firstCabinSelections?.voyagersMembersInfo &&
                state?.bookingStepsStore?.firstCabinSelections?.voyagersMembersInfo?.length > 0) ||
              (state?.bookingStepsStore?.secondCabinSelections?.voyagersMembersInfo &&
                state?.bookingStepsStore?.secondCabinSelections?.voyagersMembersInfo?.length > 0)
                ? 'yes'
                : 'no',
          },
        ],
      },
    };
  },
  'checkout_progress'
);

const trackViewCartEpic = createAnalyticsEpic(
  trackViewCart.type,
  (data, state) => {
    window.didomiOnReady = window.didomiOnReady || [];
    console.log('Didomi Wrapper trackViewCart - About to push callback');
    window.didomiOnReady.push(() => {
      console.log('Didomi Wrapper trackViewCartEpic called - Didomi is ready');
      console.log('didomiVendorsEnabled =', window.didomiState?.didomiVendorsEnabled);
      window.adobeDataLayer?.push({ ecommerce: null });
      console.log('Custom event view_cart pushed into the data layer');
    });
    return {
      step_name: 'summary',
      step_number: '7',
      area_of_payment: 'booking funnel',
      ecommerce: {
        currency: state?.genericConfigs?.configs?.b2cCommerceConfig?.currencyCode?.toLowerCase()
          ? state?.genericConfigs?.configs?.b2cCommerceConfig?.currencyCode?.toLowerCase()
          : '',
        value: getTotalPrice(state),
        items: [
          {
            ...mapItemDataBooking(data, state),
            item_category4: retrieveExperience(
              state?.bookingStepsStore?.firstCabinSelections?.selectedCategory
            ),
            package_selected:
              state?.bookingStepsStore?.selectedPriceType?.lowestPricedCategory.priceDescription?.toLowerCase()
                ? state?.bookingStepsStore?.selectedPriceType?.lowestPricedCategory.priceDescription?.toLowerCase()
                : '',
            cabin_preference_selected:
              state?.bookingStepsStore?.firstCabinSelections?.selectedCabinPreference?.toLowerCase()
                ? state?.bookingStepsStore?.firstCabinSelections?.selectedCabinPreference?.toLowerCase()
                : '',
            cabin_type_detail:
              state?.bookingStepsStore?.firstCabinSelections?.selectedCabinType?.toLowerCase()
                ? state?.bookingStepsStore?.firstCabinSelections?.selectedCabinType?.toLowerCase()
                : '',
            voyagers_club:
              (state?.bookingStepsStore?.firstCabinSelections?.voyagersMembersInfo &&
                state?.bookingStepsStore?.firstCabinSelections?.voyagersMembersInfo?.length > 0) ||
              (state?.bookingStepsStore?.secondCabinSelections?.voyagersMembersInfo &&
                state?.bookingStepsStore?.secondCabinSelections?.voyagersMembersInfo?.length > 0)
                ? 'yes'
                : 'no',
          },
        ],
      },
    };
  },
  'view_cart'
);

const trackUpgradeOfferEpic = createAnalyticsEpic(
  trackUpgradeOffer.type,
  (data, state) => {
    window.didomiOnReady = window.didomiOnReady || [];
    console.log('Didomi Wrapper trackUpgradeOffer - About to push callback');
    window.didomiOnReady.push(() => {
      console.log('Didomi Wrapper trackUpgradeOfferEpic called - Didomi is ready');
      console.log('didomiVendorsEnabled =', window.didomiState?.didomiVendorsEnabled);
      window.adobeDataLayer?.push({ ecommerce: null });
      console.log('Custom event upgrade_offer pushed into the data layer');
    });
    return {
      step_name: 'summary',
      step_number: '7',
      area_of_payment: 'booking funnel',
      ecommerce: {
        currency: state?.genericConfigs?.configs?.b2cCommerceConfig?.currencyCode?.toLowerCase()
          ? state?.genericConfigs?.configs?.b2cCommerceConfig?.currencyCode?.toLowerCase()
          : '',
        value: getTotalPrice(state),
        items: [
          {
            ...mapItemDataBooking(data, state),
            item_category4: retrieveExperience(
              state?.bookingStepsStore?.firstCabinSelections?.selectedCategory
            ),
            package_selected:
              state?.bookingStepsStore?.selectedPriceType?.lowestPricedCategory.priceDescription?.toLowerCase()
                ? state?.bookingStepsStore?.selectedPriceType?.lowestPricedCategory.priceDescription?.toLowerCase()
                : '',
            cabin_preference_selected:
              state?.bookingStepsStore?.firstCabinSelections?.selectedCabinPreference?.toLowerCase()
                ? state?.bookingStepsStore?.firstCabinSelections?.selectedCabinPreference?.toLowerCase()
                : '',
            cabin_type_detail:
              state?.bookingStepsStore?.firstCabinSelections?.selectedCabinType?.toLowerCase()
                ? state?.bookingStepsStore?.firstCabinSelections?.selectedCabinType?.toLowerCase()
                : '',
            voyagers_club:
              (state?.bookingStepsStore?.firstCabinSelections?.voyagersMembersInfo &&
                state?.bookingStepsStore?.firstCabinSelections?.voyagersMembersInfo?.length > 0) ||
              (state?.bookingStepsStore?.secondCabinSelections?.voyagersMembersInfo &&
                state?.bookingStepsStore?.secondCabinSelections?.voyagersMembersInfo?.length > 0)
                ? 'yes'
                : 'no',
          },
        ],
      },
    };
  },
  'upgrade_offer'
);

const getCabinDetail = (state: RootState, selectedPackage: { description: string }) => {
  const prevCabinType = mscGet<string>('session', 'prevCabinTypeD');

  const itemName = selectedPackage?.description?.toLowerCase()
    ? selectedPackage?.description?.toLowerCase()
    : '';

  let cabinTypeDetail;
  if (prevCabinType != '' && prevCabinType != null)
    cabinTypeDetail = prevCabinType + ' : ' + itemName;
  else cabinTypeDetail = '' + ' : ' + itemName;

  cabinTypeDetail = cabinTypeDetail.trim();
  if (cabinTypeDetail.startsWith(':')) {
    cabinTypeDetail = cabinTypeDetail.slice(1);
    cabinTypeDetail = cabinTypeDetail.trim();
  }

  if (cabinTypeDetail.trimEnd().endsWith(':')) {
    // Remove the '|' and any trailing spaces
    return cabinTypeDetail.trimEnd().slice(0, -1).trimEnd();
  }

  mscSet('session', 'prevCabinTypeD', cabinTypeDetail);
  return cabinTypeDetail;
};

const getCabinPreferenceSelected = (state: RootState, selectedPackage: { description: string }) => {
  const prevCabinPreference = mscGet<string>('session', 'prevCabinPreferenceS');

  const cabinPreference =
    state?.bookingStepsStore?.firstCabinSelections?.selectedCabinPreference?.toLowerCase()
      ? state?.bookingStepsStore?.firstCabinSelections?.selectedCabinPreference?.toLowerCase()
      : '';

  const itemName = selectedPackage?.description?.toLowerCase()
    ? selectedPackage?.description?.toLowerCase()
    : '';

  let cabinPreferenceSelected;
  if (prevCabinPreference != '' && prevCabinPreference != null)
    cabinPreferenceSelected = prevCabinPreference + ' : ' + itemName;
  else cabinPreferenceSelected = cabinPreference + ' : ' + itemName;

  cabinPreferenceSelected = cabinPreferenceSelected.trim();
  if (cabinPreferenceSelected.startsWith(':')) {
    cabinPreferenceSelected = cabinPreferenceSelected.slice(1);
    cabinPreferenceSelected = cabinPreferenceSelected.trim();
  }

  if (cabinPreferenceSelected.trimEnd().endsWith(':')) {
    // Remove the '|' and any trailing spaces
    return cabinPreferenceSelected.trimEnd().slice(0, -1).trimEnd();
  }

  mscSet('session', 'prevCabinPreferenceS', cabinPreferenceSelected);
  return cabinPreferenceSelected;
};

const trackExtraServicesEpic = createAnalyticsEpic(
  trackExtraServices.type,
  (selectedPackage, state) => {
    window.didomiOnReady = window.didomiOnReady || [];
    console.log('Didomi Wrapper trackExtraServices - About to push callback');
    window.didomiOnReady.push(() => {
      console.log('Didomi Wrapper trackExtraServicesEpic called - Didomi is ready');
      console.log('didomiVendorsEnabled =', window.didomiState?.didomiVendorsEnabled);
      window.adobeDataLayer?.push({ ecommerce: null });
      console.log('Custom event add_to_cart pushed into the data layer');
    });

    let itemLevelCoupon;
    const promotionId = mscGet<string>('session', 'promId') || '';
    if (promotionId) itemLevelCoupon = 'shrx-' + promotionId;
    else itemLevelCoupon = 'shrx-none';
    return {
      area_of_payment: 'booking funnel',
      ecommerce: {
        currency: state?.genericConfigs?.configs?.b2cCommerceConfig?.currencyCode?.toLowerCase()
          ? state?.genericConfigs?.configs?.b2cCommerceConfig?.currencyCode?.toLowerCase()
          : '',
        value: (selectedPackage as any)?.grossPrice
          ? parseFloat((selectedPackage as any)?.grossPrice)
          : '',
        items: [
          {
            ...mapItemDataBooking(selectedPackage, state),
            discount: 0,
            strikethrough: 'no',
            coupon: itemLevelCoupon,
            item_id: (selectedPackage as any)?.code?.toLowerCase()
              ? (selectedPackage as any)?.code?.toLowerCase()
              : '',
            item_name: (selectedPackage as any)?.description?.toLowerCase()
              ? (selectedPackage as any)?.description?.toLowerCase()
              : '',
            item_category: 'prepaid',
            item_category2: (selectedPackage as any)?.serviceType?.toLowerCase()
              ? (selectedPackage as any)?.serviceType?.toLowerCase()
              : '',
            item_variant: 'prepaid',
            item_category4: retrieveExperience(
              state?.bookingStepsStore?.firstCabinSelections?.selectedCategory
            ),
            price: (selectedPackage as any)?.grossPrice
              ? parseFloat((selectedPackage as any)?.grossPrice)
              : '',
            cabin_type_detail:
              state?.bookingStepsStore?.firstCabinSelections?.selectedCabinType?.toLowerCase()
                ? state?.bookingStepsStore?.firstCabinSelections?.selectedCabinType?.toLowerCase()
                : '',
            cabin_preference_selected:
              state?.bookingStepsStore?.firstCabinSelections?.selectedCabinPreference?.toLowerCase()
                ? state?.bookingStepsStore?.firstCabinSelections?.selectedCabinPreference?.toLowerCase()
                : '',
          },
        ],
      },
    };
  },
  'add_to_cart'
);

const trackExtraServicesListEpic = createAnalyticsEpic(
  trackExtraServicesList.type,
  (listData, state) => {
    window.didomiOnReady = window.didomiOnReady || [];
    console.log('Didomi Wrapper trackExtraServicesList - About to push callback');
    window.didomiOnReady.push(() => {
      console.log('Didomi Wrapper trackExtraServicesListEpic called - Didomi is ready');
      console.log('didomiVendorsEnabled =', window.didomiState?.didomiVendorsEnabled);
      window.adobeDataLayer?.push({ ecommerce: null });
      console.log('Custom event view_item_list pushed into the data layer');
    });
    const storageIndex = getStorageIndex();

    let itemLevelCoupon;
    const promotionId = mscGet<string>('session', 'promId') || '';
    if (promotionId) itemLevelCoupon = 'shrx-' + promotionId;
    else itemLevelCoupon = 'shrx-none';

    // Return the structured data
    return {
      area_of_payment: 'booking funnel',
      ecommerce: {
        items: (listData as any[]).map(
          (extraService: {
            code: string;
            description: string;
            serviceType: string;
            grossPrice: string;
          }) => ({
            ...mapItemDataBooking(extraService, state),
            discount: 0,
            strikethrough: 'no',
            coupon: itemLevelCoupon,
            item_id: extraService?.code?.toLowerCase() ? extraService?.code?.toLowerCase() : '',
            item_name: extraService?.description?.toLowerCase()
              ? extraService?.description?.toLowerCase()
              : '',
            item_category: 'prepaid',
            item_category2: extraService?.serviceType?.toLowerCase()
              ? extraService?.serviceType?.toLowerCase()
              : '',
            item_variant: 'prepaid',
            price: extraService?.grossPrice ? parseFloat(extraService?.grossPrice) : '',
            item_category4: retrieveExperience(
              state?.bookingStepsStore?.firstCabinSelections?.selectedCategory
            ),
            package_selected:
              state?.bookingStepsStore?.selectedPriceType?.lowestPricedCategory.priceDescription?.toLowerCase()
                ? state?.bookingStepsStore?.selectedPriceType?.lowestPricedCategory.priceDescription?.toLowerCase()
                : '',
            cabin_preference_selected:
              state?.bookingStepsStore?.firstCabinSelections?.selectedCabinPreference?.toLowerCase()
                ? state?.bookingStepsStore?.firstCabinSelections?.selectedCabinPreference?.toLowerCase()
                : '',
            cabin_type_detail:
              state?.bookingStepsStore?.firstCabinSelections?.selectedCabinType?.toLowerCase()
                ? state?.bookingStepsStore?.firstCabinSelections?.selectedCabinType?.toLowerCase()
                : '',
            voyagers_club:
              (state?.bookingStepsStore?.firstCabinSelections?.voyagersMembersInfo &&
                state?.bookingStepsStore?.firstCabinSelections?.voyagersMembersInfo?.length > 0) ||
              (state?.bookingStepsStore?.secondCabinSelections?.voyagersMembersInfo &&
                state?.bookingStepsStore?.secondCabinSelections?.voyagersMembersInfo?.length > 0)
                ? 'yes'
                : 'no',
            index: storageIndex,
          })
        ),
      },
    };
  },
  'view_item_list'
);

const trackPassengersDetailsEpic = createAnalyticsEpic(
  trackPassengersDetails.type,
  (data, state) => {
    const storageIndex = getStorageIndex();
    /**
     * If the guest selects one or more extra package, this analytic has to sent as itemsArray:
     *    - un object for the cruise (see below cruiseItem)
     *    - as many objects as there are packages selected (see below extraServicesItems)
     */
    const cruiseItem = {
      ...mapItemDataBooking(data, state),
      item_category4: retrieveExperience(
        state?.bookingStepsStore?.firstCabinSelections?.selectedCategory
      ),
      package_selected:
        state?.bookingStepsStore?.selectedPriceType?.lowestPricedCategory.priceDescription?.toLowerCase()
          ? state?.bookingStepsStore?.selectedPriceType?.lowestPricedCategory.priceDescription?.toLowerCase()
          : '',
      cabin_preference_selected:
        state?.bookingStepsStore?.firstCabinSelections?.selectedCabinPreference?.toLowerCase()
          ? state?.bookingStepsStore?.firstCabinSelections?.selectedCabinPreference?.toLowerCase()
          : '',
      cabin_type_detail:
        state?.bookingStepsStore?.firstCabinSelections?.selectedCabinType?.toLowerCase()
          ? state?.bookingStepsStore?.firstCabinSelections?.selectedCabinType?.toLowerCase()
          : '',
      voyagers_club:
        (state?.bookingStepsStore?.firstCabinSelections?.voyagersMembersInfo &&
          state?.bookingStepsStore?.firstCabinSelections?.voyagersMembersInfo?.length > 0) ||
        (state?.bookingStepsStore?.secondCabinSelections?.voyagersMembersInfo &&
          state?.bookingStepsStore?.secondCabinSelections?.voyagersMembersInfo?.length > 0)
          ? 'yes'
          : 'no',
    };

    let itemLevelCoupon;
    const promotionId = mscGet<string>('session', 'promId') || '';
    if (promotionId) itemLevelCoupon = 'shrx-' + promotionId;
    else itemLevelCoupon = 'shrx-none';

    const extraServices =
      state?.bookingStepsStore?.firstCabinSelections?.selectedExtraServices || [];
    const extraServicesItems = extraServices.map((extraService) => ({
      ...mapItemDataBooking(extraService, state),
      discount: 0,
      strikethrough: 'no',
      coupon: itemLevelCoupon,
      item_id: extraService?.code?.toLowerCase() ? extraService?.code?.toLowerCase() : '',
      item_name: extraService?.description?.toLowerCase()
        ? extraService?.description?.toLowerCase()
        : '',
      item_category: 'prepaid',
      item_category2: extraService?.serviceType?.toLowerCase()
        ? extraService?.serviceType?.toLowerCase()
        : '',
      item_variant: 'prepaid',
      price: extraService?.grossPrice ? parseFloat(extraService?.grossPrice) : '',

      item_category4: retrieveExperience(
        state?.bookingStepsStore?.firstCabinSelections?.selectedCategory
      ),
      package_selected:
        state?.bookingStepsStore?.selectedPriceType?.lowestPricedCategory.priceDescription?.toLowerCase()
          ? state?.bookingStepsStore?.selectedPriceType?.lowestPricedCategory.priceDescription?.toLowerCase()
          : '',
      cabin_preference_selected:
        state?.bookingStepsStore?.firstCabinSelections?.selectedCabinPreference?.toLowerCase()
          ? state?.bookingStepsStore?.firstCabinSelections?.selectedCabinPreference?.toLowerCase()
          : '',
      cabin_type_detail:
        state?.bookingStepsStore?.firstCabinSelections?.selectedCabinType?.toLowerCase()
          ? state?.bookingStepsStore?.firstCabinSelections?.selectedCabinType?.toLowerCase()
          : '',
      voyagers_club:
        (state?.bookingStepsStore?.firstCabinSelections?.voyagersMembersInfo &&
          state?.bookingStepsStore?.firstCabinSelections?.voyagersMembersInfo?.length > 0) ||
        (state?.bookingStepsStore?.secondCabinSelections?.voyagersMembersInfo &&
          state?.bookingStepsStore?.secondCabinSelections?.voyagersMembersInfo?.length > 0)
          ? 'yes'
          : 'no',
      index: storageIndex,
    }));
    window.didomiOnReady = window.didomiOnReady || [];
    console.log('Didomi Wrapper trackPassengersDetails - About to push callback');
    window.didomiOnReady.push(() => {
      console.log('Didomi Wrapper trackPassengersDetailsEpic called - Didomi is ready');
      console.log('didomiVendorsEnabled =', window.didomiState?.didomiVendorsEnabled);
      window.adobeDataLayer?.push({ ecommerce: null });
      console.log('Custom event add_shipping_info pushed into the data layer');
    });
    return {
      step_name: 'passengers details',
      step_number: '8',
      area_of_payment: 'booking funnel',
      ecommerce: {
        currency: state?.genericConfigs?.configs?.b2cCommerceConfig?.currencyCode?.toLowerCase()
          ? state?.genericConfigs?.configs?.b2cCommerceConfig?.currencyCode?.toLowerCase()
          : '',
        value: getTotalPrice(state),
        items: [{ ...cruiseItem, index: storageIndex }, ...extraServicesItems],
      },
    };
  },

  'add_shipping_info'
);

const trackInsuranceInfoEpic = createAnalyticsEpic(
  trackInsuranceInfo.type,
  (data, state) => {
    const storageIndex = getStorageIndex();
    const cruiseItem = {
      ...mapItemDataBooking(data, state),
      item_category4: retrieveExperience(
        state?.bookingStepsStore?.firstCabinSelections?.selectedCategory
      ),
      package_selected:
        state?.bookingStepsStore?.selectedPriceType?.lowestPricedCategory.priceDescription?.toLowerCase()
          ? state?.bookingStepsStore?.selectedPriceType?.lowestPricedCategory.priceDescription?.toLowerCase()
          : '',
      cabin_preference_selected:
        state?.bookingStepsStore?.firstCabinSelections?.selectedCabinPreference?.toLowerCase()
          ? state?.bookingStepsStore?.firstCabinSelections?.selectedCabinPreference?.toLowerCase()
          : '',
      cabin_type_detail:
        state?.bookingStepsStore?.firstCabinSelections?.selectedCabinType?.toLowerCase()
          ? state?.bookingStepsStore?.firstCabinSelections?.selectedCabinType?.toLowerCase()
          : '',
      voyagers_club:
        (state?.bookingStepsStore?.firstCabinSelections?.voyagersMembersInfo &&
          state?.bookingStepsStore?.firstCabinSelections?.voyagersMembersInfo?.length > 0) ||
        (state?.bookingStepsStore?.secondCabinSelections?.voyagersMembersInfo &&
          state?.bookingStepsStore?.secondCabinSelections?.voyagersMembersInfo?.length > 0)
          ? 'yes'
          : 'no',
    };

    let itemLevelCoupon;
    const promotionId = mscGet<string>('session', 'promId') || '';
    if (promotionId) itemLevelCoupon = 'shrx-' + promotionId;
    else itemLevelCoupon = 'shrx-none';

    const extraServices =
      state?.bookingStepsStore?.firstCabinSelections?.selectedExtraServices || [];
    const extraServicesItems = extraServices.map((extraService) => ({
      ...mapItemDataBooking(extraService, state),
      discount: 0,
      strikethrough: 'no',
      coupon: itemLevelCoupon,
      item_id: extraService?.code?.toLowerCase() ? extraService?.code?.toLowerCase() : '',
      item_name: extraService?.description?.toLowerCase()
        ? extraService?.description?.toLowerCase()
        : '',
      item_category: 'prepaid',
      item_category2: extraService?.serviceType?.toLowerCase()
        ? extraService?.serviceType?.toLowerCase()
        : '',
      item_variant: 'prepaid',
      price: extraService?.grossPrice ? parseFloat(extraService?.grossPrice) : '',

      item_category4: retrieveExperience(
        state?.bookingStepsStore?.firstCabinSelections?.selectedCategory
      ),
      package_selected:
        state?.bookingStepsStore?.selectedPriceType?.lowestPricedCategory.priceDescription?.toLowerCase()
          ? state?.bookingStepsStore?.selectedPriceType?.lowestPricedCategory.priceDescription?.toLowerCase()
          : '',
      cabin_preference_selected:
        state?.bookingStepsStore?.firstCabinSelections?.selectedCabinPreference?.toLowerCase()
          ? state?.bookingStepsStore?.firstCabinSelections?.selectedCabinPreference?.toLowerCase()
          : '',
      cabin_type_detail:
        state?.bookingStepsStore?.firstCabinSelections?.selectedCabinType?.toLowerCase()
          ? state?.bookingStepsStore?.firstCabinSelections?.selectedCabinType?.toLowerCase()
          : '',
      voyagers_club:
        (state?.bookingStepsStore?.firstCabinSelections?.voyagersMembersInfo &&
          state?.bookingStepsStore?.firstCabinSelections?.voyagersMembersInfo?.length > 0) ||
        (state?.bookingStepsStore?.secondCabinSelections?.voyagersMembersInfo &&
          state?.bookingStepsStore?.secondCabinSelections?.voyagersMembersInfo?.length > 0)
          ? 'yes'
          : 'no',
      index: storageIndex,
    }));
    window.didomiOnReady = window.didomiOnReady || [];
    window.didomiOnReady.push(() => {
      window.adobeDataLayer.push({ ecommerce: null });
    });
    return {
      event: 'checkout_progress',
      step_name: 'insurance',
      step_number: getInsuranceStepNumber(state),
      area_of_payment: 'booking funnel',
      ecommerce: {
        currency: state?.genericConfigs?.configs?.b2cCommerceConfig?.currencyCode?.toLowerCase()
          ? state?.genericConfigs?.configs?.b2cCommerceConfig?.currencyCode?.toLowerCase()
          : '',
        value: getTotalPrice(state),
        items: [
          {
            ...cruiseItem,
            index: storageIndex,
            ...getInsuranceAttributesForInsuranceStep(state),
          },
          ...extraServicesItems,
        ],
      },
    };
  },
  'checkout_progress'
);

// Exporting the epics for redux-observable middleware
export const analyticsEpic = [
  trackInsuranceInfoEpic,
  trackAddPaymentInfoEpic,
  trackPurchaseEventEpic,
  trackHoldOptionEventEpic,
  trackPurchaseFailedEventEpic,
  trackPurchaseDuringCheckoutEpic,
  trackSearchExcursionEventEpic,
  trackSelectItemEpic,
  trackPageViewEpic,
  trackSearchCruiseEpic,
  trackViewItemListEpic,
  trackAddToWishlistEpic,
  trackCruiseShareEpic,
  trackAddToCartEpic,
  trackViewItemEpic,
  trackCheckoutProgressGuestAndCabinEpic,
  trackBeginCheckoutEpic,
  trackCheckoutCabinTypeSelectionEpic,
  trackCheckoutCabinPreferencesEpic,
  trackCheckoutCabinTypeDetailsEpic,
  trackCheckoutCabinLocationEpic,
  trackViewCartEpic,
  trackUpgradeOfferEpic,
  trackExtraServicesEpic,
  trackPassengersDetailsEpic,
  trackExtraServicesListEpic,
];
